<template>
  <AuthenticatedView
    :loading="loading && !result"
    :current-user="currentUser"
    :has-result="!!result"
  >
    <AppHeader
      v-if="currentUser"
      :user="currentUser"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div
      ref="containerRef"
      :class="[
        'card',
        'calendar-container',
        { 'full-width': isFullWidth, 'mobile-summary-mode': isSummaryTilesMode },
      ]"
    >
      <breadcrumbs :items="breadcrumbsItems" />
      <div class="calendar-header">
        <h2 class="calendar-header-title">{{ s__('Calendar|Calendar') }}</h2>
      </div>

      <div ref="periodHeaderRef" class="period-header">
        <button
          class="nav-btn period-nav-btn"
          :title="s__('Calendar|Previous')"
          @click="prevPeriod"
        >
          ‹
        </button>
        <button
          type="button"
          class="period-header-button"
          :title="s__('Calendar|Select month')"
          :aria-label="s__('Calendar|Select month')"
          @click="toggleMonthPicker"
        >
          {{ periodTitle }}
        </button>
        <button class="nav-btn period-nav-btn" :title="s__('Calendar|Next')" @click="nextPeriod">
          ›
        </button>
        <div v-if="showMonthPicker" class="period-month-picker-popover" @click.stop>
          <VueDatePicker
            v-model="monthPickerDate"
            month-picker
            :inline="true"
            :auto-apply="true"
            :enable-time-picker="false"
            :clearable="false"
            :locale="monthPickerLocale"
            :teleport="false"
            @update:model-value="handleMonthPickerSelect"
          />
        </div>
      </div>

      <!-- Show checkboxes by default (not in expandable section) -->
      <div class="filter-panel">
        <div class="filter-group">
          <div class="filter-checkboxes calendar-filter-checkboxes-row">
            <label class="filter-checkbox item-type-checkbox events-checkbox">
              <input v-model="showEvents" type="checkbox" />
              <span>{{ s__('Calendar|Events') }}</span>
            </label>
            <label class="filter-checkbox item-type-checkbox deadlines-checkbox">
              <input v-model="showTaskDeadlines" type="checkbox" />
              <span>{{ s__('Calendar|Task deadlines') }}</span>
            </label>
            <button
              type="button"
              class="info-icon-button"
              @click="showCalendarInfoModal = true"
              :title="s__('Calendar|Calendar Information')"
              :aria-label="s__('Calendar|Calendar Information')"
            >
              <InfoIcon />
            </button>
            <button
              v-if="canToggleFullWidth"
              type="button"
              class="btn-secondary btn-sm calendar-full-width-toggle-btn"
              @click="toggleFullWidth"
              :title="
                isFullWidth
                  ? s__('Calendar|Exit full-width mode')
                  : s__('Calendar|Enter full-width mode')
              "
            >
              {{
                isFullWidth
                  ? s__('Calendar|Exit full-width mode')
                  : s__('Calendar|Enter full-width mode')
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Task filters expandable section (only visible when showTaskDeadlines is true) -->
      <div v-if="showTaskDeadlines" class="filter-panel">
        <div class="filter-header" @click="toggleTaskFiltersExpanded">
          <h3 style="margin: 0; font-size: 14px; font-weight: 600">
            {{ s__('Calendar|Task Filters') }}
            <span v-if="activeTaskFiltersCount > 0" class="active-count"
              >({{ activeTaskFiltersCount }})</span
            >
          </h3>
          <span class="expand-icon">{{ isTaskFiltersExpanded ? '▼' : '▶' }}</span>
        </div>

        <div v-if="isTaskFiltersExpanded" class="filter-content" @click.stop>
          <div class="filters-row">
            <div class="filter-group">
              <label class="filter-label">{{ s__('Calendar|Show tasks where I am') }}:</label>
              <div class="filter-checkboxes mobile-underlined-checklist">
                <label class="filter-checkbox mobile-underlined-checklist-item">
                  <input v-model="filterAsAuthor" type="checkbox" />
                  <span>{{ s__('Calendar|Author') }}</span>
                </label>
                <label class="filter-checkbox mobile-underlined-checklist-item">
                  <input v-model="filterAsAssignee" type="checkbox" />
                  <span>{{ s__('Calendar|Assignee') }}</span>
                </label>
                <label class="filter-checkbox mobile-underlined-checklist-item">
                  <input v-model="filterAsSubscriber" type="checkbox" />
                  <span>{{ s__('Calendar|Subscriber') }}</span>
                </label>
              </div>
            </div>

            <div v-if="projects.length > 0" class="filter-group">
              <MultiSelect
                :key="`projects-${projects.length}`"
                :options="projectOptions"
                :selected-ids="selectedProjectIds"
                :label="s__('Calendar|Filter by projects')"
                :placeholder="s__('Calendar|Select projects...')"
                :search-placeholder="s__('Calendar|Search projects...')"
                :no-results-text="s__('Calendar|No projects found')"
                @update:selected-ids="handleProjectFilterChange"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        ref="calendarWrapperRef"
        class="calendar-wrapper"
        @touchstart.passive="handleCalendarTouchStart"
        @touchmove.passive="handleCalendarTouchMove"
        @touchend.passive="handleCalendarTouchEnd"
        @touchcancel.passive="resetCalendarSwipeState"
        @click.capture="handleCalendarClickCapture"
      >
        <!-- Loading indicator - overlay on top of calendar -->
        <div v-if="loading" class="loading-overlay">
          <span>{{ __('Loading…') }}</span>
        </div>
        <FullCalendar ref="calendarRef" :options="calendarOptions" />
      </div>

      <!-- Add Event Button (floating) -->
      <button
        v-if="showEvents"
        ref="addEventBtnRef"
        class="add-event-btn btn-primary"
        :title="s__('CalendarEvent|New Event')"
        :style="{ right: buttonRightOffset + 'px' }"
        @click="openCreateEventModal"
      >
        <PlusIcon v-if="isXsScreen" :size="20" color="#fff" />
        <span v-else class="add-event-text">{{ s__('CalendarEvent|New Event') }}</span>
      </button>

      <div
        v-if="showDayDetailsPanel"
        class="day-details-panel"
        @touchstart.passive="handleDayDetailsTouchStart"
        @touchmove.passive="handleDayDetailsTouchMove"
        @touchend.passive="handleDayDetailsTouchEnd"
        @touchcancel.passive="resetDayDetailsSwipeState"
        @click.capture="handleDayDetailsClickCapture"
      >
        <div class="day-details-header">
          <h3 class="day-details-title">
            {{ s__('Calendar|Day details') }}
          </h3>
          <span class="day-details-date">{{ selectedDayDisplayTitle }}</span>
        </div>

        <div class="day-details-grid">
          <div v-if="!showEvents && !showTaskDeadlines" class="day-details-empty">
            {{ s__('Calendar|Enable events or task deadlines to see day details') }}
          </div>

          <div v-if="showEvents" class="day-details-section">
            <h4 class="day-details-section-title">{{ s__('Calendar|Events') }}</h4>
            <div v-if="selectedDayEvents.length === 0" class="day-details-empty">
              {{ s__('Calendar|No events for this day') }}
            </div>
            <div v-else class="day-details-table-wrap">
              <table class="day-details-table">
                <thead>
                  <tr>
                    <th>{{ s__('Calendar|Time') }}</th>
                    <th>{{ s__('Calendar|Title') }}</th>
                    <th>{{ s__('Calendar|Description') }}</th>
                    <th>{{ s__('Calendar|Place') }}</th>
                    <th>{{ s__('Calendar|Participants') }}</th>
                    <th>{{ s__('Calendar|Project') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="eventItem in selectedDayEvents"
                    :key="`day-event-${eventItem.id}`"
                    class="day-details-row day-details-row-event"
                    :style="{ '--day-details-row-accent': getEventRowAccentColor(eventItem) }"
                    @click="openEditEventModal(eventItem)"
                  >
                    <td>{{ formatEventTimeRangeLabel(eventItem) }}</td>
                    <td>
                      <div
                        class="day-details-event-title"
                        :title="eventIndicatorMeta(eventItem).title"
                      >
                        <component
                          :is="eventIndicatorMeta(eventItem).icon"
                          :title="eventIndicatorMeta(eventItem).title"
                          :tooltip="eventIndicatorMeta(eventItem).title"
                          class="day-details-event-indicator-icon"
                          :color="eventIndicatorMeta(eventItem).color"
                          aria-hidden="true"
                        />
                        <span>{{ eventItem.title }}</span>
                      </div>
                    </td>
                    <td
                      class="day-details-cell-ellipsis"
                      :title="eventItem.description || s__('Calendar|No description')"
                    >
                      {{ truncateText(eventItem.description, 120) }}
                    </td>
                    <td class="day-details-cell-ellipsis" :title="eventItem.location || '—'">
                      {{ truncateText(eventItem.location, 60) }}
                    </td>
                    <td
                      class="day-details-cell-ellipsis"
                      :title="formatEventParticipantsLabel(eventItem, 20)"
                    >
                      {{ formatEventParticipantsLabel(eventItem, 4) }}
                    </td>
                    <td>
                      <a
                        v-if="eventItem.project?.id"
                        :href="buildProjectDashboardUrl(eventItem.project.id)"
                        class="day-details-project-link"
                        @click.stop="storeCalendarUrl"
                        >{{ eventItem.project?.title || '—' }}</a
                      >
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="showTaskDeadlines" class="day-details-section">
            <h4 class="day-details-section-title">{{ s__('Calendar|Task deadlines') }}</h4>
            <div v-if="selectedDayDeadlines.length === 0" class="day-details-empty">
              {{ s__('Calendar|No task deadlines for this day') }}
            </div>
            <div v-else class="day-details-table-wrap">
              <table class="day-details-table">
                <thead>
                  <tr>
                    <th>{{ s__('Calendar|Time') }}</th>
                    <th></th>
                    <th>{{ s__('Calendar|Title') }}</th>
                    <th>{{ s__('Calendar|Description') }}</th>
                    <th>{{ s__('Calendar|Author') }}</th>
                    <th>{{ s__('Calendar|Assignee') }}</th>
                    <th>{{ s__('Calendar|Project') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="taskItem in selectedDayDeadlines"
                    :key="`day-task-${taskItem.id}`"
                    class="day-details-row"
                    @click="openTaskFromDayDetails(taskItem)"
                  >
                    <td>{{ formatTaskTimeLabel(taskItem) }}</td>
                    <td>
                      <PriorityIcon
                        v-if="taskItem.priority"
                        :priority="taskItem.priority"
                        :size="14"
                        :title="taskItem.priority"
                      />
                      <span v-else>—</span>
                    </td>
                    <td>{{ taskItem.title }}</td>
                    <td
                      class="day-details-cell-ellipsis"
                      :title="taskItem.description || s__('Calendar|No description')"
                    >
                      {{ truncateText(taskItem.description, 120) }}
                    </td>
                    <td>{{ formatPersonLabel(taskItem.author) }}</td>
                    <td>{{ formatPersonLabel(taskItem.assignee) }}</td>
                    <td>
                      <a
                        v-if="taskItem.project?.id"
                        :href="buildProjectDashboardUrl(taskItem.project.id)"
                        class="day-details-project-link"
                        @click.stop="storeCalendarUrl"
                        >{{ taskItem.project?.title || '—' }}</a
                      >
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Alert Banner -->
      <AlertBanner
        v-if="alertBanner.title"
        :type="alertBanner.type"
        :title="alertBanner.title"
        :message="alertBanner.message"
        @dismiss="alertBanner = { title: '', message: '', type: 'info' }"
      />

      <!-- Event Modal -->
      <CalendarEventModal
        :visible="showEventModal"
        :event="editingEvent"
        :projects="projects"
        :initial-date="eventInitialDate"
        :locale="userLocale"
        :timezone="userTimezone"
        :read-only="editingEvent && !editingEvent.canUpdate"
        @close="closeEventModal"
        @saved="handleEventSaved"
        @deleted="handleEventDeleted"
        @responded="handleEventResponded"
      />

      <!-- Calendar Info Modal -->
      <CalendarInfoModal :visible="showCalendarInfoModal" @close="showCalendarInfoModal = false" />
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, loadTranslations } from '@/locale'
import FullCalendar from '@fullcalendar/vue3'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import luxonPlugin from '@fullcalendar/luxon3'
import ruLocale from '@fullcalendar/core/locales/ru'
import enLocale from '@fullcalendar/core/locales/en-gb'
import { enUS as dateFnsEnUS, ru as dateFnsRu } from 'date-fns/locale'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import CalendarEventModal from './CalendarEventModal.vue'
import AppHeader from '../common/AppHeader.vue'
import MultiSelect from '../common/MultiSelect.vue'
import PlusIcon from '../common/icons/PlusIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import PriorityIcon from '../common/icons/PriorityIcon.vue'
import CalendarWeekFillIcon from '../common/icons/CalendarWeekFillIcon.vue'
import CalendarEventFillIcon from '../common/icons/CalendarEventFillIcon.vue'
import CalendarEventOutlineIcon from '../common/icons/CalendarEventOutlineIcon.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import AlertBanner from '../common/AlertBanner.vue'
import CalendarInfoModal from './CalendarInfoModal.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { buildTaskUrl, navigateToUrl } from '@/utils/navigation'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'
import '../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  chatId: {
    type: String,
    default: null,
  },
  fromId: {
    type: String,
    default: null,
  },
})

function isAllowedCalendarView(view) {
  return view === 'month'
}

function getFallbackCalendarView() {
  return 'month'
}

function normalizeCalendarView(view) {
  return isAllowedCalendarView(view) ? view : getFallbackCalendarView()
}

const calendarRef = ref(null)
const calendarWrapperRef = ref(null)
const addEventBtnRef = ref(null)
const containerRef = ref(null)
const periodHeaderRef = ref(null)
const showMonthPicker = ref(false)
const monthPickerDate = ref(new Date())

// Button right offset to align with container's right edge
const buttonRightOffset = ref(20) // Initial value - will be recalculated on mount

// Calculate button position relative to container
function updateButtonPosition() {
  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    nextTick(() => {
      const container = document.querySelector('.calendar-container')
      if (!container) {
        // Retry on next animation frame if container not found
        requestAnimationFrame(() => {
          updateButtonPosition()
        })
        return
      }

      // Check actual DOM state, not reactive value (DOM might not be updated yet)
      const isFullWidthInDOM = container.classList.contains('full-width')

      // If full-width mode, button should be 20px from viewport edge
      if (isFullWidthInDOM) {
        buttonRightOffset.value = 20
        // Force the style with !important to override any CSS rules
        const button = document.querySelector('.add-event-btn')
        if (button) {
          button.style.setProperty('right', '20px', 'important')
        }
        return
      }

      // In regular mode, position button relative to container's right edge
      const containerRect = container.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const containerRight = containerRect.right
      const rightOffset = viewportWidth - containerRight + 20 // 20px padding from container edge
      const finalOffset = Math.max(20, rightOffset) // Minimum 20px from viewport edge

      buttonRightOffset.value = finalOffset

      // Force the style with !important to override any CSS rules
      const button = document.querySelector('.add-event-btn')
      if (button) {
        button.style.setProperty('right', finalOffset + 'px', 'important')
      }
    })
  })
}

// Detect if screen is mobile/small
const isMobile = ref(false)
function checkMobile() {
  isMobile.value = window.innerWidth < 768 // Common mobile breakpoint
}

const calendarSwipeStartX = ref(null)
const calendarSwipeStartY = ref(null)
const suppressCalendarClickUntil = ref(0)
const dayDetailsSwipeStartX = ref(null)
const dayDetailsSwipeStartY = ref(null)
const suppressDayDetailsClickUntil = ref(0)
// Feature flag: set to true to re-enable mobile month swipe navigation.
const ENABLE_MOBILE_MONTH_SWIPE = false

function resetCalendarSwipeState() {
  calendarSwipeStartX.value = null
  calendarSwipeStartY.value = null
}

function getCalendarHorizontalScrollState() {
  const wrapper = calendarWrapperRef.value
  if (!(wrapper instanceof HTMLElement)) {
    return { isScrollable: false, isAtStart: true, isAtEnd: true }
  }

  const maxScrollLeft = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth)
  const edgeThreshold = 8
  return {
    isScrollable: maxScrollLeft > 1,
    isAtStart: wrapper.scrollLeft <= edgeThreshold,
    isAtEnd: wrapper.scrollLeft >= maxScrollLeft - edgeThreshold,
  }
}

function isMobileMonthSwipeEnabled() {
  return ENABLE_MOBILE_MONTH_SWIPE && currentView.value === 'month'
}

function handleCalendarTouchStart(event) {
  if (!isMobile.value || !isMobileMonthSwipeEnabled() || !event.touches?.length) return

  const touch = event.touches[0]
  calendarSwipeStartX.value = touch.clientX
  calendarSwipeStartY.value = touch.clientY
}

function handleCalendarTouchMove(event) {
  if (!isMobile.value || !isMobileMonthSwipeEnabled()) return
  if (calendarSwipeStartX.value === null || calendarSwipeStartY.value === null) return

  const touch = event.touches?.[0]
  if (!touch) return

  const deltaX = Math.abs(touch.clientX - calendarSwipeStartX.value)
  const deltaY = Math.abs(touch.clientY - calendarSwipeStartY.value)

  // Intentionally kept for gesture warmup; final decision is made in touchend.
  if (deltaX >= 24 && deltaX > deltaY) return
}

function handleCalendarTouchEnd(event) {
  if (!isMobile.value || !isMobileMonthSwipeEnabled() || showEventModal.value) {
    resetCalendarSwipeState()
    return
  }

  if (calendarSwipeStartX.value === null || calendarSwipeStartY.value === null) {
    resetCalendarSwipeState()
    return
  }

  const touch = event.changedTouches?.[0]
  if (!touch) {
    resetCalendarSwipeState()
    return
  }

  const target = event.target
  if (
    target instanceof HTMLElement &&
    target.closest('button, a, input, select, textarea, .fc-event')
  ) {
    resetCalendarSwipeState()
    return
  }

  const deltaX = touch.clientX - calendarSwipeStartX.value
  const deltaY = touch.clientY - calendarSwipeStartY.value
  const absX = Math.abs(deltaX)
  const absY = Math.abs(deltaY)

  // Trigger period navigation only for clear horizontal gestures.
  if (absX >= 56 && absX > absY * 1.4) {
    const scrollState = getCalendarHorizontalScrollState()
    if (scrollState.isScrollable) {
      const canNavigatePrev = deltaX > 0 && scrollState.isAtStart
      const canNavigateNext = deltaX < 0 && scrollState.isAtEnd
      if (!canNavigatePrev && !canNavigateNext) {
        resetCalendarSwipeState()
        return
      }
    }

    suppressCalendarClickUntil.value = Date.now() + 500
    if (deltaX > 0) {
      prevPeriod()
    } else {
      nextPeriod()
    }
  }

  resetCalendarSwipeState()
}

function handleCalendarClickCapture(event) {
  if (Date.now() >= suppressCalendarClickUntil.value) return

  event.preventDefault()
  event.stopPropagation()
}

function resetDayDetailsSwipeState() {
  dayDetailsSwipeStartX.value = null
  dayDetailsSwipeStartY.value = null
}

function parseDateKeyToDate(dateKey) {
  if (!dateKey || typeof dateKey !== 'string') return null
  const [year, month, day] = dateKey.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day, 0, 0, 0, 0)
}

function buildSelectedDayForMonth(targetDate) {
  const monthDate = targetDate instanceof Date ? targetDate : new Date(targetDate)
  if (isNaN(monthDate.getTime())) return null

  const selectedDate = parseDateKeyToDate(selectedDayDateKey.value)
  const preferredDay = selectedDate ? selectedDate.getDate() : monthDate.getDate()
  const year = monthDate.getFullYear()
  const month = monthDate.getMonth()
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate()
  const safeDay = Math.min(Math.max(preferredDay, 1), lastDayOfMonth)
  return new Date(year, month, safeDay, 0, 0, 0, 0)
}

function syncSelectedDayToCurrentMonth(targetDate = currentDate.value) {
  const alignedDate = buildSelectedDayForMonth(targetDate)
  if (!alignedDate) return
  selectedDayDateKey.value = formatDateForCalendar(alignedDate)
}

function shiftSelectedDayBy(daysDelta) {
  const baseDate = parseDateKeyToDate(selectedDayDateKey.value) || new Date(currentDate.value)
  if (isNaN(baseDate.getTime())) return

  const targetDate = new Date(baseDate)
  targetDate.setDate(targetDate.getDate() + daysDelta)
  targetDate.setHours(0, 0, 0, 0)
  const targetDateString = formatDateForCalendar(targetDate)
  const currentMonthChanged =
    targetDate.getFullYear() !== currentDate.value.getFullYear() ||
    targetDate.getMonth() !== currentDate.value.getMonth()

  selectedDayDateKey.value = targetDateString

  if (currentMonthChanged && calendarRef.value?.getApi) {
    resetNavigationState()
    navigationState.isNavigating = true
    navigationState.targetDate = new Date(targetDate)
    currentDate.value = new Date(targetDate)
    calendarRef.value.getApi().gotoDate(targetDateString)
  }

  if (currentMonthChanged && !calendarRef.value?.getApi) {
    currentDate.value = new Date(targetDate)
  }

  storeCalendarUrl()
}

function handleDayDetailsTouchStart(event) {
  if (!isMobile.value || !event.touches?.length || showEventModal.value) return

  const touch = event.touches[0]
  dayDetailsSwipeStartX.value = touch.clientX
  dayDetailsSwipeStartY.value = touch.clientY
}

function handleDayDetailsTouchMove(event) {
  if (!isMobile.value) return
  if (dayDetailsSwipeStartX.value === null || dayDetailsSwipeStartY.value === null) return

  const touch = event.touches?.[0]
  if (!touch) return

  const deltaX = Math.abs(touch.clientX - dayDetailsSwipeStartX.value)
  const deltaY = Math.abs(touch.clientY - dayDetailsSwipeStartY.value)
  if (deltaX >= 24 && deltaX > deltaY) return
}

function handleDayDetailsTouchEnd(event) {
  if (!isMobile.value || showEventModal.value) {
    resetDayDetailsSwipeState()
    return
  }

  if (dayDetailsSwipeStartX.value === null || dayDetailsSwipeStartY.value === null) {
    resetDayDetailsSwipeState()
    return
  }

  const touch = event.changedTouches?.[0]
  if (!touch) {
    resetDayDetailsSwipeState()
    return
  }

  const target = event.target
  if (target instanceof HTMLElement && target.closest('button, a, input, select, textarea')) {
    resetDayDetailsSwipeState()
    return
  }

  const deltaX = touch.clientX - dayDetailsSwipeStartX.value
  const deltaY = touch.clientY - dayDetailsSwipeStartY.value
  const absX = Math.abs(deltaX)
  const absY = Math.abs(deltaY)

  if (absX >= 56 && absX > absY * 1.4) {
    suppressDayDetailsClickUntil.value = Date.now() + 500
    if (deltaX > 0) {
      shiftSelectedDayBy(-1)
    } else {
      shiftSelectedDayBy(1)
    }
  }

  resetDayDetailsSwipeState()
}

function handleDayDetailsClickCapture(event) {
  if (Date.now() >= suppressDayDetailsClickUntil.value) return

  event.preventDefault()
  event.stopPropagation()
}

// Detect if screen is xs (640px breakpoint)
const isXsScreen = ref(false)
function checkXsScreen() {
  isXsScreen.value = window.innerWidth < 640
}

function getFullCalendarView(view) {
  if (!isAllowedCalendarView(view)) return 'dayGridMonth'
  return 'dayGridMonth'
}

function getCalendarViewKey(fullCalendarView) {
  if (fullCalendarView === 'timeGridDay') return 'day'
  if (fullCalendarView === 'timeGridWeek' || fullCalendarView === 'dayGridWeek') return 'week'
  if (fullCalendarView === 'dayGridMonth') return 'month'
  return null
}

function isWeekFullCalendarView(fullCalendarView) {
  return fullCalendarView === 'timeGridWeek' || fullCalendarView === 'dayGridWeek'
}

// Set default view based on screen size
const getDefaultView = () => {
  checkMobile()
  return 'month'
}

// Helper function to restore calendar state from URL or sessionStorage
function restoreCalendarState() {
  // Initialize today date
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)

  // Default state
  const defaultState = {
    view: getDefaultView(),
    date: today,
    selectedDayDate: today,
    selectedProjectIds: [],
    filterAsAuthor: true,
    filterAsAssignee: true,
    filterAsSubscriber: true,
    showEvents: true,
    showTaskDeadlines: false, // Default to false unless explicitly set in session
  }

  // First, try to restore from URL parameters
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const viewParam = urlParams.get('view')
    const dateParam = urlParams.get('date')
    const selectedDayParam = urlParams.get('selectedDay')
    const projectIdsParam = urlParams.get('projectIds')
    const asAuthorParam = urlParams.get('asAuthor')
    const asAssigneeParam = urlParams.get('asAssignee')
    const asSubscriberParam = urlParams.get('asSubscriber')
    const showEventsParam = urlParams.get('showEvents')
    const showTaskDeadlinesParam = urlParams.get('showTaskDeadlines')

    if (viewParam && ['day', 'week', 'month'].includes(viewParam)) {
      const state = { ...defaultState }
      state.view = normalizeCalendarView(viewParam)
      let date = today

      if (dateParam) {
        // Parse date from YYYY-MM-DD format
        const dateParts = dateParam.split('-')
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0], 10)
          const month = parseInt(dateParts[1], 10) - 1 // Month is 0-indexed
          const day = parseInt(dateParts[2], 10)
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            date = new Date(year, month, day, 0, 0, 0, 0)
          }
        }
      }
      state.date = date
      state.selectedDayDate = date

      // Restore filters
      if (projectIdsParam) {
        state.selectedProjectIds = projectIdsParam.split(',').filter(Boolean)
      }
      if (asAuthorParam !== null) state.filterAsAuthor = asAuthorParam === 'true'
      if (asAssigneeParam !== null) state.filterAsAssignee = asAssigneeParam === 'true'
      if (asSubscriberParam !== null) state.filterAsSubscriber = asSubscriberParam === 'true'
      if (showEventsParam !== null) state.showEvents = showEventsParam === 'true'
      if (showTaskDeadlinesParam !== null)
        state.showTaskDeadlines = showTaskDeadlinesParam === 'true'
      if (selectedDayParam) {
        const selectedDayParts = selectedDayParam.split('-')
        if (selectedDayParts.length === 3) {
          const year = parseInt(selectedDayParts[0], 10)
          const month = parseInt(selectedDayParts[1], 10) - 1
          const day = parseInt(selectedDayParts[2], 10)
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            state.selectedDayDate = new Date(year, month, day, 0, 0, 0, 0)
          }
        }
      }

      return state
    }
  } catch (e) {
    // Failed to restore calendar state from URL - use defaults
  }

  // Fallback: try to restore from sessionStorage
  try {
    const storedUrl = sessionStorage.getItem('calendarUrl')
    if (storedUrl) {
      const urlObj = new URL(storedUrl, window.location.origin)
      const viewParam = urlObj.searchParams.get('view')
      const dateParam = urlObj.searchParams.get('date')
      const selectedDayParam = urlObj.searchParams.get('selectedDay')
      const projectIdsParam = urlObj.searchParams.get('projectIds')
      const asAuthorParam = urlObj.searchParams.get('asAuthor')
      const asAssigneeParam = urlObj.searchParams.get('asAssignee')
      const asSubscriberParam = urlObj.searchParams.get('asSubscriber')
      const showEventsParam = urlObj.searchParams.get('showEvents')
      const showTaskDeadlinesParam = urlObj.searchParams.get('showTaskDeadlines')

      if (viewParam && ['day', 'week', 'month'].includes(viewParam)) {
        const state = { ...defaultState }
        state.view = normalizeCalendarView(viewParam)
        let date = today

        if (dateParam) {
          // Parse date from YYYY-MM-DD format
          const dateParts = dateParam.split('-')
          if (dateParts.length === 3) {
            const year = parseInt(dateParts[0], 10)
            const month = parseInt(dateParts[1], 10) - 1 // Month is 0-indexed
            const day = parseInt(dateParts[2], 10)
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              date = new Date(year, month, day, 0, 0, 0, 0)
            }
          }
        }
        state.date = date
        state.selectedDayDate = date

        // Restore filters
        if (projectIdsParam) {
          state.selectedProjectIds = projectIdsParam.split(',').filter(Boolean)
        }
        if (asAuthorParam !== null) state.filterAsAuthor = asAuthorParam === 'true'
        if (asAssigneeParam !== null) state.filterAsAssignee = asAssigneeParam === 'true'
        if (asSubscriberParam !== null) state.filterAsSubscriber = asSubscriberParam === 'true'
        if (showEventsParam !== null) state.showEvents = showEventsParam === 'true'
        if (showTaskDeadlinesParam !== null)
          state.showTaskDeadlines = showTaskDeadlinesParam === 'true'
        if (selectedDayParam) {
          const selectedDayParts = selectedDayParam.split('-')
          if (selectedDayParts.length === 3) {
            const year = parseInt(selectedDayParts[0], 10)
            const month = parseInt(selectedDayParts[1], 10) - 1
            const day = parseInt(selectedDayParts[2], 10)
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              state.selectedDayDate = new Date(year, month, day, 0, 0, 0, 0)
            }
          }
        }

        return state
      }
    }
  } catch (e) {
    // Failed to restore calendar state from sessionStorage - use defaults
  }

  // Default: use today and default view
  return defaultState
}

// Restore calendar state from URL or sessionStorage
const restoredState = restoreCalendarState()
const currentView = ref(normalizeCalendarView(restoredState.view))
// Initialize to restored date or start of today in local timezone to avoid timezone issues
// Use Date constructor with year, month, date to ensure local timezone
const currentDate = ref(restoredState.date)
const selectedDayDateKey = ref(
  formatDateForCalendar(restoredState.selectedDayDate || restoredState.date)
)
const selectedProjectIds = ref(restoredState.selectedProjectIds)
const projects = ref([])
// Track if projects have been initialized to prevent re-selecting all after user unchecks them
const projectsInitialized = ref(false)

// Format projects for MultiSelect
const projectOptions = computed(() => {
  return projects.value.map(p => ({
    id: p.id,
    title: p.title,
    color: '#6B7280',
  }))
})
// User role filters - restored from state or defaults
const filterAsAuthor = ref(restoredState.filterAsAuthor)
const filterAsAssignee = ref(restoredState.filterAsAssignee)
const filterAsSubscriber = ref(restoredState.filterAsSubscriber)
// Item type filters - restored from state or defaults
const showEvents = ref(restoredState.showEvents)
const showTaskDeadlines = ref(restoredState.showTaskDeadlines)
// Task filters panel state
const isTaskFiltersExpanded = ref(false)

// Compute active task filters count (for task filters expandable section)
const activeTaskFiltersCount = computed(() => {
  let count = 0
  // Count non-default task filter states
  if (!filterAsAuthor.value) count++
  if (!filterAsAssignee.value) count++
  if (!filterAsSubscriber.value) count++
  if (selectedProjectIds.value.length > 0) count++
  return count
})

function toggleTaskFiltersExpanded(event) {
  // Don't toggle if click is inside filter-content
  const target = event?.target
  if (target) {
    // Check if target is inside a .filter-content element
    const filterContent = target.closest?.('.filter-content')
    if (filterContent && filterContent !== event?.currentTarget) {
      return
    }

    // Only allow toggle if click is on the filter-header itself
    const filterHeader = target.closest?.('.filter-header')
    if (filterHeader !== event?.currentTarget) {
      return
    }
  }

  isTaskFiltersExpanded.value = !isTaskFiltersExpanded.value
}

// Store raw data from GraphQL
const rawCalendarTasks = ref([])
const rawCalendarEvents = ref([])
// Event modal state
const showEventModal = ref(false)
const showCalendarInfoModal = ref(false)
const editingEvent = ref(null)
const eventInitialDate = ref(null)
const alertBanner = ref({
  title: '',
  message: '',
  type: 'info',
})
// Initialize timezone to browser's timezone to prevent re-initialization when GraphQL returns
const browserTimezone =
  typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC'
const userTimezone = ref(browserTimezone)
// Initialize locale from browser language, fallback to 'en'
const browserLocale =
  typeof navigator !== 'undefined' && navigator.language ? navigator.language.substring(0, 2) : 'en'
const userLocale = ref(browserLocale === 'ru' ? 'ru' : 'en')
const calendarEvents = ref([]) // Store events for calendar

// Navigation state - track when we're in the middle of navigation
// to prevent handleDatesSet from overriding our changes
let navigationState = {
  isNavigating: false,
  isChangingView: false, // true when changing views, false when doing prev/next navigation
  targetDate: null,
  navigationTimeout: null,
}

function clearNavigationWaitHandle() {
  if (navigationState.navigationTimeout) {
    cancelAnimationFrame(navigationState.navigationTimeout)
    navigationState.navigationTimeout = null
  }
}

function resetNavigationState() {
  isUpdatingFromCalendar = false
  navigationState.isNavigating = false
  navigationState.isChangingView = false
  navigationState.targetDate = null
  clearNavigationWaitHandle()
}

function runAfterNextPaint(callback) {
  nextTick(() => {
    requestAnimationFrame(() => {
      callback()
    })
  })
}

function runAfterTwoPaints(callback) {
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        callback()
      })
    })
  })
}

function refreshCalendarLayout() {
  runAfterTwoPaints(() => {
    if (calendarRef.value?.getApi) {
      calendarRef.value.getApi().updateSize()
    }
    updateButtonPosition()
    requestAnimationFrame(() => {
      updateButtonPosition()
    })
  })
}

// GraphQL query for calendar tasks and events
const GET_CALENDAR_DATA = gql`
  query GetCalendarData(
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime!
    $includeTasks: Boolean!
    $projectIds: [ID!]
    $asAuthor: Boolean
    $asAssignee: Boolean
    $asSubscriber: Boolean
  ) {
    currentUser {
      id
      name
      username
      preferredTimezone
      preferredLanguage
      projects(first: 100) {
        edges {
          node {
            id
            title
            isPersonal
          }
        }
      }
    }
    calendarTasks(
      startDate: $startDate
      endDate: $endDate
      projectIds: $projectIds
      asAuthor: $asAuthor
      asAssignee: $asAssignee
      asSubscriber: $asSubscriber
    ) @include(if: $includeTasks) {
      id
      iid
      title
      description
      dueAt
      priority
      archived
      project {
        id
        title
        isPersonal
      }
      taskCategory {
        id
        name
      }
      assignee {
        id
        username
        name
      }
      author {
        id
        username
        name
      }
      workflowStatus {
        id
        name
        color
        isFinal
      }
    }
    calendarEvents(startDate: $startDate, endDate: $endDate, projectIds: $projectIds) {
      id
      title
      description
      startAt
      endAt
      allDay
      seriesId
      occurrenceDate
      detached
      location
      color
      isPersonal
      reminderMinutes
      canUpdate
      ended
      project {
        id
        title
        isPersonal
      }
      author {
        id
        username
        name
      }
      participantsCount
      eventParticipants {
        id
        role
        responseStatus
        user {
          id
          username
          name
        }
      }
      currentUserParticipant {
        id
        role
        responseStatus
      }
    }
  }
`

// Helper to get week start day based on locale
// Russian and most European locales: Monday (1)
// English (US): Sunday (0)
const getWeekStartDay = () => {
  return userLocale.value === 'ru' ? 1 : 0
}

// Calculate date range based on current view and date
const dateRange = computed(() => {
  // Create new Date objects to avoid mutating currentDate
  // Use local timezone constructor to ensure correct date
  const baseDate = new Date(currentDate.value)
  const year = baseDate.getFullYear()
  const month = baseDate.getMonth()
  const date = baseDate.getDate()

  let start, end

  switch (currentView.value) {
    case 'day':
      start = new Date(year, month, date, 0, 0, 0, 0)
      end = new Date(year, month, date, 23, 59, 59, 999)
      break
    case 'week':
      // Get start of week based on locale (Monday for ru, Sunday for en)
      const weekBase = new Date(baseDate)
      const dayOfWeek = weekBase.getDay()
      const weekStartDay = getWeekStartDay()
      // Calculate days to subtract to get to week start
      // For Monday start: if today is Sunday (0), we need to go back 6 days
      // For Sunday start: just use dayOfWeek directly
      let daysToSubtract
      if (weekStartDay === 1) {
        // Monday start
        daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      } else {
        // Sunday start
        daysToSubtract = dayOfWeek
      }
      weekBase.setDate(weekBase.getDate() - daysToSubtract)
      start = new Date(weekBase.getFullYear(), weekBase.getMonth(), weekBase.getDate(), 0, 0, 0, 0)
      // Get end of week - add 6 days to start
      const weekEnd = new Date(start)
      weekEnd.setDate(weekEnd.getDate() + 6)
      end = new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate(), 23, 59, 59, 999)
      break
    case 'month':
      // Get start of month
      start = new Date(year, month, 1, 0, 0, 0, 0)
      // Get end of month - first day of next month, then subtract 1 day
      end = new Date(year, month + 1, 0, 23, 59, 59, 999)
      break
  }

  const result = {
    start: start.toISOString(),
    end: end.toISOString(),
  }

  return result
})

// Create reactive variables for the query as a ref (Apollo reacts better to refs)
function buildTaskRoleVariables(includeTasks, asAuthor, asAssignee, asSubscriber) {
  if (!includeTasks) {
    return {}
  }

  return {
    asAuthor,
    asAssignee,
    asSubscriber,
  }
}

const queryVariables = ref({
  startDate: dateRange.value.start,
  endDate: dateRange.value.end,
  includeTasks: showTaskDeadlines.value,
  projectIds: selectedProjectIds.value.length > 0 ? selectedProjectIds.value : null,
  ...buildTaskRoleVariables(
    showTaskDeadlines.value,
    filterAsAuthor.value,
    filterAsAssignee.value,
    filterAsSubscriber.value
  ),
})

// GraphQL query - use reactive ref
const { result, loading, error, refetch } = useQuery(GET_CALENDAR_DATA, queryVariables, {
  fetchPolicy: 'network-only', // Always fetch fresh data to ensure session cookie is used
  notifyOnNetworkStatusChange: true,
})

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

// Current user for AppHeader
const currentUser = computed(() => result.value?.currentUser || null)

// Watch for errors
watch(error, err => {
  if (err) {
    // GraphQL query error - handled by UI
  }
})

// Watch for result changes
watch(result, data => {
  if (data) {
    // Use user's preferredTimezone if set, otherwise keep browser timezone
    const preferredTz = data.currentUser?.preferredTimezone
    if (preferredTz) {
      userTimezone.value = preferredTz
    }

    // Use user's preferredLanguage if set, otherwise keep browser locale
    const preferredLang = data.currentUser?.preferredLanguage
    if (preferredLang) {
      // Map language codes to supported locales
      const locale = preferredLang === 'ru' ? 'ru' : 'en'
      userLocale.value = locale
      // Load translations and update document language
      document.documentElement.lang = preferredLang
      loadTranslations(locale)
    }

    // Process projects from connection structure
    const projectEdges = data.currentUser?.projects?.edges || []
    const fetchedProjects = projectEdges
      .map(edge => edge.node)
      .sort((a, b) => {
        // Personal projects first
        if (a.isPersonal && !b.isPersonal) return -1
        if (!a.isPersonal && b.isPersonal) return 1
        // Then by title
        return (a.title || '').localeCompare(b.title || '')
      })
    projects.value = fetchedProjects

    // Initialize selected projects (all selected by default) only on first load
    // If restored state has empty projectIds, keep it empty (user explicitly deselected all)
    // If restored state has projectIds, keep them
    // Only auto-select all if we have no restored state, no projects selected, and haven't initialized yet
    if (
      !projectsInitialized.value &&
      selectedProjectIds.value.length === 0 &&
      projects.value.length > 0
    ) {
      // Check if we have a restored state - if restored state explicitly had empty projectIds,
      // we should keep it empty. Otherwise, select all projects.
      const hasRestoredProjectIds = restoredState.selectedProjectIds.length > 0
      if (!hasRestoredProjectIds) {
        // No restored project IDs - select all by default
        selectedProjectIds.value = projects.value.map(p => p.id)
        // Force update queryVariables with the new project IDs
        // This ensures the next query has correct projectIds
        queryVariables.value = {
          ...queryVariables.value,
          projectIds: selectedProjectIds.value,
        }
      } else {
        // We have restored project IDs, but they might not match current projects
        // Filter to only include projects that still exist
        const validProjectIds = selectedProjectIds.value.filter(id =>
          projects.value.some(p => p.id === id)
        )
        if (validProjectIds.length !== selectedProjectIds.value.length) {
          selectedProjectIds.value = validProjectIds
          queryVariables.value = {
            ...queryVariables.value,
            projectIds: selectedProjectIds.value.length > 0 ? selectedProjectIds.value : null,
          }
        }
      }
      // Mark as initialized to prevent re-selecting all on subsequent refetches
      projectsInitialized.value = true
    } else if (!projectsInitialized.value && projects.value.length > 0) {
      // Projects were already selected (from restored state or user interaction)
      // Just mark as initialized
      projectsInitialized.value = true
    }

    // Store raw data
    rawCalendarTasks.value = data.calendarTasks || []
    rawCalendarEvents.value = data.calendarEvents || []

    // Update calendar with combined data
    updateCalendarDisplay()
  }
})

// Watch dateRange and selectedProjectIds to update queryVariables
// This will trigger Apollo to refetch automatically
watch(
  [
    dateRange,
    selectedProjectIds,
    showTaskDeadlines,
    filterAsAuthor,
    filterAsAssignee,
    filterAsSubscriber,
  ],
  ([newRange, newProjectIds, includeTasks, asAuthor, asAssignee, asSubscriber], oldValues) => {
    // Skip on initial load (when oldValues is undefined)
    if (!oldValues || !oldValues[0]) {
      return
    }

    const newProjectIdsArray = newProjectIds.length > 0 ? [...newProjectIds] : null

    // Update queryVariables - Apollo will detect the ref change and refetch
    // This ensures data is fetched for the correct date range
    queryVariables.value = {
      startDate: newRange.start,
      endDate: newRange.end,
      includeTasks,
      projectIds: newProjectIdsArray,
      ...buildTaskRoleVariables(includeTasks, asAuthor, asAssignee, asSubscriber),
    }
  },
  { deep: true, immediate: false }
)

// Watch show filters to update display (no refetch needed) and store URL
watch([showEvents, showTaskDeadlines], () => {
  updateCalendarDisplay()
  storeCalendarUrl()
})

// Watch filter changes to store URL
watch([filterAsAuthor, filterAsAssignee, filterAsSubscriber], () => {
  storeCalendarUrl()
})

// Watch project filter changes to store URL
watch(
  selectedProjectIds,
  () => {
    storeCalendarUrl()
  },
  { deep: true }
)

// Watch calendarEvents and update calendar when they change
watch(
  calendarEvents,
  newEvents => {
    runAfterNextPaint(() => {
      renderCalendarEvents(newEvents)
    })
  },
  { deep: true, immediate: false }
)

// Note: View changes are handled by queryVariables watcher above
// since dateRange depends on currentView, changing view will update dateRange
// which will update queryVariables and trigger refetch

// Note: The query will automatically refetch when variables (dateRange, selectedProjectIds) change
// No need for manual watch/refetch - Apollo handles this automatically

// Period title for display (localized)
const periodTitle = computed(() => {
  const date = currentDate.value
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  // Use Intl.DateTimeFormat for localized month names
  const locale = userLocale.value === 'ru' ? 'ru-RU' : 'en-US'

  let title
  if (currentView.value === 'month') {
    // Format: "November 2025" or "Ноябрь 2025" (capitalized)
    const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    const formatted = formatter.format(date)
    // Capitalize first letter (important for Russian where month names are lowercase)
    title = formatted.charAt(0).toUpperCase() + formatted.slice(1)
  } else if (currentView.value === 'week') {
    // Get week start based on locale (Monday for ru, Sunday for en)
    const weekStart = new Date(date)
    const dayOfWeek = weekStart.getDay()
    const weekStartDay = getWeekStartDay()
    let daysToSubtract
    if (weekStartDay === 1) {
      // Monday start
      daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    } else {
      // Sunday start
      daysToSubtract = dayOfWeek
    }
    weekStart.setDate(weekStart.getDate() - daysToSubtract)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)

    const formatDate = d =>
      `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`
    title = `${formatDate(weekStart)} - ${formatDate(weekEnd)}`
  } else {
    // Day view
    title = `${day.toString().padStart(2, '0')}.${(month + 1).toString().padStart(2, '0')}.${year}`
  }

  return title
})

const monthPickerLocale = computed(() => {
  return userLocale.value === 'ru' ? dateFnsRu : dateFnsEnUS
})

const isSummaryTilesMode = computed(() => {
  return currentView.value === 'month'
})

const showDayDetailsPanel = computed(() => {
  return currentView.value === 'month'
})

const selectedDayDisplayTitle = computed(() => {
  if (!selectedDayDateKey.value) return ''
  const [year, month, day] = selectedDayDateKey.value.split('-').map(Number)
  const selectedDate = new Date(year, (month || 1) - 1, day || 1)
  const locale = userLocale.value === 'ru' ? 'ru-RU' : 'en-US'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(selectedDate)
})

function hasTaskSpecificTime(task) {
  if (!task?.dueAt) return false
  const dueDate = new Date(task.dueAt)
  if (isNaN(dueDate.getTime())) return false
  const utcHours = dueDate.getUTCHours()
  const utcMinutes = dueDate.getUTCMinutes()
  const utcSeconds = dueDate.getUTCSeconds()
  return utcHours !== 0 || utcMinutes !== 0 || utcSeconds !== 0
}

function compareCalendarDayItems(a, b, allDayA, allDayB, dateA, dateB) {
  if (allDayA !== allDayB) {
    return allDayA ? -1 : 1
  }
  const tsA = dateA?.getTime?.() || 0
  const tsB = dateB?.getTime?.() || 0
  return tsA - tsB
}

const selectedDayEvents = computed(() => {
  if (!showEvents.value || !selectedDayDateKey.value) return []
  return rawCalendarEvents.value
    .filter(event => {
      if (!event?.startAt) return false
      return formatDateForCalendar(new Date(event.startAt)) === selectedDayDateKey.value
    })
    .slice()
    .sort((a, b) =>
      compareCalendarDayItems(
        a,
        b,
        !!a.allDay,
        !!b.allDay,
        new Date(a.startAt),
        new Date(b.startAt)
      )
    )
})

const selectedDayDeadlines = computed(() => {
  if (!showTaskDeadlines.value || !selectedDayDateKey.value) return []
  return rawCalendarTasks.value
    .filter(task => {
      if (!task?.dueAt) return false
      return formatDateForCalendar(new Date(task.dueAt)) === selectedDayDateKey.value
    })
    .slice()
    .sort((a, b) =>
      compareCalendarDayItems(
        a,
        b,
        !hasTaskSpecificTime(a),
        !hasTaskSpecificTime(b),
        new Date(a.dueAt),
        new Date(b.dueAt)
      )
    )
})

const mobileDayCounts = computed(() => {
  const counts = new Map()
  calendarEvents.value.forEach(event => {
    if (!event?.start) return
    const start = new Date(event.start)
    if (isNaN(start.getTime())) return
    const dateKey = formatDateForCalendar(start)
    const current = counts.get(dateKey) || { events: 0, deadlines: 0 }
    if (event.extendedProps?.itemType === 'event') {
      current.events += 1
    } else if (event.extendedProps?.itemType === 'task') {
      current.deadlines += 1
    }
    counts.set(dateKey, current)
  })
  return counts
})

function renderSummaryDayTiles() {
  if (!isSummaryTilesMode.value) return
  const calendarRoot = calendarRef.value?.$el
  if (!calendarRoot) return

  const dayCells = calendarRoot.querySelectorAll('.fc-daygrid-day')
  dayCells.forEach(cell => {
    const dateKey = cell.getAttribute('data-date')
    if (!dateKey) return
    const dayFrame = cell.querySelector('.fc-daygrid-day-frame')
    if (!dayFrame) return
    const dayNumber = Number(dateKey.slice(-2))
    const isSelected = dateKey === selectedDayDateKey.value
    const isOutsideMonth = cell.classList.contains('fc-day-other')
    const dayData = mobileDayCounts.value.get(dateKey) || { events: 0, deadlines: 0 }
    const eventsChip = `<span class="mobile-day-summary-chip mobile-day-summary-chip-events" title="${s__('Calendar|Events')}">${dayData.events}</span>`
    const deadlinesChip = `<span class="mobile-day-summary-chip mobile-day-summary-chip-deadlines" title="${s__('Calendar|Task deadlines')}">${dayData.deadlines}</span>`
    const hasEventsMetric = showEvents.value
    const hasDeadlinesMetric = showTaskDeadlines.value
    const visibleMetricsCount = Number(hasEventsMetric) + Number(hasDeadlinesMetric)

    let tileLayoutClass = `mobile-day-summary-tile-single${isOutsideMonth ? ' is-outside-month' : ''}`
    let countsMarkup = ''

    if (isOutsideMonth) {
      countsMarkup = `
        <div class="mobile-day-summary-counts mobile-day-summary-counts-single">
          <div class="mobile-day-summary-date">${dayNumber}</div>
        </div>
      `
    } else if (visibleMetricsCount >= 2) {
      tileLayoutClass = 'mobile-day-summary-tile-double'
      countsMarkup = `
        <div class="mobile-day-summary-counts mobile-day-summary-counts-double">
          <div class="mobile-day-summary-date mobile-day-summary-double-date">${dayNumber}</div>
          <div class="mobile-day-summary-double-spacer" aria-hidden="true"></div>
          <div class="mobile-day-summary-double-events">${eventsChip}</div>
          <div class="mobile-day-summary-double-deadlines">${deadlinesChip}</div>
        </div>
      `
    } else {
      const singleChip = hasEventsMetric ? eventsChip : hasDeadlinesMetric ? deadlinesChip : ''
      countsMarkup = `
        <div class="mobile-day-summary-counts mobile-day-summary-counts-single">
          <div class="mobile-day-summary-date">${dayNumber}</div>
          ${singleChip}
        </div>
      `
    }

    const tileInteractiveAttrs = isOutsideMonth
      ? 'role="presentation" tabindex="-1"'
      : `role="button" tabindex="0" aria-label="${s__('Calendar|Open day details')}" aria-pressed="${isSelected}"`

    dayFrame.innerHTML = `
      <div class="mobile-day-summary-tile ${tileLayoutClass} ${isSelected ? 'is-selected' : ''}" ${tileInteractiveAttrs}>
        ${countsMarkup}
      </div>
    `
  })
}

function handleDateClick(info) {
  if (currentView.value !== 'month') return

  const dayEl = info?.dayEl
  const dayCell = dayEl?.closest?.('.fc-daygrid-day') ?? dayEl
  if (dayCell?.classList?.contains('fc-day-other')) return

  if (info?.dateStr && /^\d{4}-\d{2}-\d{2}/.test(info.dateStr)) {
    selectedDayDateKey.value = info.dateStr.slice(0, 10)
    return
  }
  const clickedDate = new Date(info.date)
  if (isNaN(clickedDate.getTime())) return
  selectedDayDateKey.value = formatDateForCalendar(clickedDate)
}

// Calendar options - STATIC to prevent re-initialization
// Use ref instead of computed to prevent reactivity from re-initializing FullCalendar
const calendarInitDate = new Date()
const todayForCalendar = new Date(
  calendarInitDate.getFullYear(),
  calendarInitDate.getMonth(),
  calendarInitDate.getDate(),
  0,
  0,
  0,
  0
)

// Determine initial view based on restored state or screen size
const getInitialView = () => {
  // Use restored view if available, otherwise use screen size
  if (restoredState.view) {
    return getFullCalendarView(restoredState.view)
  }
  return 'dayGridWeek'
}

// Use restored date for initial date
const initialDateForCalendar = restoredState.date
  ? formatDateForCalendar(restoredState.date)
  : formatDateForCalendar(todayForCalendar)

// Helper function to get SVG icon HTML for participation status
// Uses the same SVG paths as the icon components in common/icons/
function getParticipationIcon(status) {
  const iconSize = 14
  switch (status) {
    case 'organizer':
      // CrownIcon - purple-400
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="event-icon event-icon-organizer" style="color: #a78bfa;" aria-hidden="true"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path><path d="M5 21h14"></path></svg>`
    case 'accepted':
      // CheckSimpleIcon - green-400
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="event-icon event-icon-accepted" style="color: #4ade80;" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>`
    case 'pending':
      // ClockIcon - gray-400
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="event-icon event-icon-pending" style="color: #9ca3af;" aria-hidden="true"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>`
    case 'declined':
      // XIcon - red
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="event-icon event-icon-declined" style="color: #db4437;" aria-hidden="true"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>`
    default:
      return ''
  }
}

// Event content renderer to add SVG icons
// Wrapped in try-catch to prevent errors from blocking calendar initialization
function renderEventContent(arg) {
  try {
    // Defensive checks - return null to use default rendering if anything is missing
    if (!arg || !arg.event) {
      return null // Use default FullCalendar rendering
    }

    const { event, timeText } = arg
    const itemType = event.extendedProps?.itemType

    // For task deadlines, explicitly render the title (don't return null)
    if (itemType === 'task') {
      const container = document.createElement('div')
      container.className = 'event-content-wrapper'
      container.style.display = 'flex'
      container.style.flexDirection = 'column'
      container.style.alignItems = 'flex-start'
      container.style.gap = '2px'
      container.style.width = '100%'
      container.style.minWidth = '0'
      container.style.overflow = 'hidden'

      // Add title text - ensure it's visible with white color
      const titleRow = document.createElement('div')
      titleRow.style.display = 'flex'
      titleRow.style.alignItems = 'center'
      titleRow.style.gap = '4px'
      titleRow.style.width = '100%'
      titleRow.style.minWidth = '0'

      const textWrapper = document.createElement('span')
      textWrapper.textContent = event.title || ''
      textWrapper.style.flex = '1 1 auto'
      textWrapper.style.minWidth = '0'
      textWrapper.style.overflow = 'hidden'
      textWrapper.style.textOverflow = 'ellipsis'
      textWrapper.style.whiteSpace = 'nowrap'
      textWrapper.style.color = '#ffffff'
      textWrapper.style.fontWeight = '500'
      textWrapper.style.display = 'block'
      titleRow.appendChild(textWrapper)
      container.appendChild(titleRow)

      // Add time text on next line if present (for time grid view)
      if (timeText) {
        const timeWrapper = document.createElement('div')
        timeWrapper.textContent = timeText
        timeWrapper.style.width = '100%'
        timeWrapper.style.fontSize = '0.75em'
        timeWrapper.style.opacity = '0.9'
        container.appendChild(timeWrapper)
      }

      return { domNodes: [container] }
    }

    // Only apply custom rendering for calendar events
    if (itemType !== 'event') {
      return null // Use default FullCalendar rendering for other event types
    }

    const participationStatus = event.extendedProps?.participationStatus

    // Always render calendar events with custom content to ensure title is visible
    // If no participation status, render without icon

    // Create container for icon and text
    const container = document.createElement('div')
    container.className = 'event-content-wrapper'
    container.style.display = 'flex'
    container.style.flexDirection = 'column'
    container.style.alignItems = 'flex-start'
    container.style.gap = '2px'
    container.style.width = '100%'
    container.style.minWidth = '0'
    container.style.overflow = 'hidden'

    // Create title row with icon and text
    const titleRow = document.createElement('div')
    titleRow.style.display = 'flex'
    titleRow.style.alignItems = 'center'
    titleRow.style.gap = '4px'
    titleRow.style.width = '100%'
    titleRow.style.minWidth = '0'

    // Add icon only if participation status exists
    if (participationStatus) {
      const icon = getParticipationIcon(participationStatus)
      if (icon) {
        const iconWrapper = document.createElement('span')
        iconWrapper.innerHTML = icon
        iconWrapper.style.display = 'inline-flex'
        iconWrapper.style.alignItems = 'center'
        iconWrapper.style.flexShrink = '0'
        iconWrapper.style.flexGrow = '0'
        titleRow.appendChild(iconWrapper)
      }
    }

    // Add title text - ensure it's visible with white color
    const textWrapper = document.createElement('span')
    textWrapper.textContent = event.title || ''
    textWrapper.style.flex = '1 1 auto'
    textWrapper.style.minWidth = '0'
    textWrapper.style.overflow = 'hidden'
    textWrapper.style.textOverflow = 'ellipsis'
    textWrapper.style.whiteSpace = 'nowrap'
    textWrapper.style.color = '#ffffff'
    textWrapper.style.fontWeight = '500'
    textWrapper.style.display = 'block'
    titleRow.appendChild(textWrapper)
    container.appendChild(titleRow)

    // Add time text on next line if present (for time grid view)
    if (timeText) {
      const timeWrapper = document.createElement('div')
      timeWrapper.textContent = timeText
      timeWrapper.style.width = '100%'
      timeWrapper.style.fontSize = '0.75em'
      timeWrapper.style.opacity = '0.9'
      container.appendChild(timeWrapper)
    }

    return { domNodes: [container] }
  } catch (error) {
    // If anything goes wrong, fall back to default FullCalendar rendering
    // Don't log in production to avoid console noise
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error rendering event content:', error)
    }
    return null // Use default FullCalendar rendering
  }
}

const calendarOptions = ref({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin],
  initialView: getInitialView(),
  initialDate: initialDateForCalendar,
  // Use browser timezone initially, will be updated to user's preferred timezone
  timeZone: browserTimezone,
  // Localization - include all locales, set initial based on browser
  locales: [ruLocale, enLocale],
  locale: browserLocale === 'ru' ? 'ru' : 'en',
  headerToolbar: false, // We have custom header
  fixedWeekCount: false,
  height: 'auto',
  eventDisplay: 'block',
  eventTimeFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  slotMinTime: '00:00:00',
  slotMaxTime: '24:00:00',
  allDaySlot: true,
  nowIndicator: true,
  editable: false, // Will enable in Phase 3 (drag-and-drop)
  selectable: false,
  eventClick: handleEventClick,
  dateClick: handleDateClick,
  datesSet: handleDatesSet,
  // Use eventContent callback to add participation status icons
  // Wrapped in a function to ensure it's not called during initialization if there's an error
  eventContent: arg => {
    try {
      return renderEventContent(arg)
    } catch (error) {
      // Silently fall back to default rendering if callback fails
      return null
    }
  },
})

// Update calendar timezone when user timezone is loaded
// With luxon plugin, FullCalendar supports named timezones like 'Europe/Moscow'
watch(userTimezone, newTz => {
  if (newTz && calendarRef.value?.getApi) {
    const calendarApi = calendarRef.value.getApi()
    calendarApi.setOption('timeZone', newTz)
    // Re-render the calendar with new timezone
    calendarApi.render()
  }
})

// Update calendar locale when user language is loaded
// Russian locale automatically sets week start to Monday
watch(userLocale, newLocale => {
  if (newLocale && calendarRef.value?.getApi) {
    const calendarApi = calendarRef.value.getApi()
    calendarApi.setOption('locale', newLocale)
    // Re-render the calendar with new locale
    calendarApi.render()
  }
})

// Color map for calendar events
const EVENT_COLOR_MAP = {
  blue: '#4285f4',
  green: '#0f9d58',
  red: '#db4437',
  yellow: '#f4b400',
  purple: '#9c27b0',
  teal: '#009688',
  orange: '#ff5722',
  pink: '#e91e63',
  indigo: '#3f51b5',
  cyan: '#00bcd4',
  brown: '#795548',
  gray: '#607d8b',
}

// Update calendar display based on current filters
function updateCalendarDisplay() {
  const events = []

  // Add task deadlines if enabled
  if (showTaskDeadlines.value) {
    const taskEvents = convertTasksToEvents(rawCalendarTasks.value)
    events.push(...taskEvents)
  }

  // Add calendar events if enabled
  if (showEvents.value) {
    const calendarEventItems = convertCalendarEventsToEvents(rawCalendarEvents.value)
    events.push(...calendarEventItems)
  }

  calendarEvents.value = events
  if (isSummaryTilesMode.value) {
    renderCalendarEvents([])
    nextTick(() => {
      renderSummaryDayTiles()
    })
  } else {
    renderCalendarEvents(events)
  }
}

// Convert task deadlines to FullCalendar events
function convertTasksToEvents(tasks) {
  let skippedCount = 0
  const skippedReasons = {}

  const events = tasks
    .map(task => {
      const dueDate = task.dueAt ? new Date(task.dueAt) : null
      if (!dueDate || isNaN(dueDate.getTime())) {
        skippedCount++
        const reason = !task.dueAt ? 'no dueAt' : 'invalid date'
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1
        return null
      }

      // Determine if task is overdue
      const now = new Date()
      const isOverdue = dueDate < now && !task.archived

      // Determine color based on priority and overdue status
      let color = '#757575' // Default gray
      if (isOverdue) {
        color = '#c62828' // Red for overdue
      } else {
        switch (task.priority) {
          case 'highest':
            color = '#d32f2f'
            break
          case 'high':
            color = '#f57c00'
            break
          case 'medium':
            color = '#1976d2'
            break
          case 'low':
            color = '#388e3c'
            break
          case 'lowest':
            color = '#9e9e9e'
            break
        }
      }

      // Check if task has a specific time (not just a date)
      const utcHours = dueDate.getUTCHours()
      const utcMinutes = dueDate.getUTCMinutes()
      const utcSeconds = dueDate.getUTCSeconds()
      const hasTime = utcHours !== 0 || utcMinutes !== 0 || utcSeconds !== 0

      return {
        id: `task-${task.id}`,
        title: `📋 ${task.title}`,
        start: dueDate.toISOString(),
        allDay: !hasTime,
        backgroundColor: color,
        borderColor: color,
        textColor: '#ffffff',
        extendedProps: {
          taskIid: task.iid,
          task: task,
          isDeadline: true,
          itemType: 'task',
        },
        classNames: ['task-deadline'],
        display: 'block',
      }
    })
    .filter(Boolean)

  return events
}

// Convert calendar events to FullCalendar events
function convertCalendarEventsToEvents(calendarEventsList) {
  return calendarEventsList.map(event => {
    const startDate = new Date(event.startAt)
    const endDate = event.endAt ? new Date(event.endAt) : null
    // If event.color is already a hex value (starts with #), use it directly
    // Otherwise, try to look it up in the color map (for backward compatibility)
    const color = event.color?.startsWith('#')
      ? event.color
      : EVENT_COLOR_MAP[event.color] || EVENT_COLOR_MAP.blue

    // Determine user's participation status (data already fetched in GraphQL query)
    const classNames = ['calendar-event']
    let participationStatus = null
    const currentUserParticipant = event.currentUserParticipant

    if (currentUserParticipant) {
      if (currentUserParticipant.role === 'organizer') {
        classNames.push('event-organizer')
        participationStatus = 'organizer'
      } else {
        switch (currentUserParticipant.responseStatus) {
          case 'accepted':
            classNames.push('event-accepted')
            participationStatus = 'accepted'
            break
          case 'pending':
            classNames.push('event-pending')
            participationStatus = 'pending'
            break
          case 'declined':
            classNames.push('event-declined')
            participationStatus = 'declined'
            break
        }
      }
    }

    return {
      id: `event-${event.id}`,
      title: event.title,
      start: startDate.toISOString(),
      end: endDate ? endDate.toISOString() : undefined,
      allDay: event.allDay,
      backgroundColor: color,
      borderColor: color,
      textColor: '#ffffff',
      extendedProps: {
        eventId: event.id,
        event: event,
        isDeadline: false,
        itemType: 'event',
        participationStatus: participationStatus,
      },
      classNames: classNames,
      display: 'block',
    }
  })
}

// Render events to the calendar
function renderCalendarEvents(events) {
  if (calendarRef.value?.getApi) {
    const calendarApi = calendarRef.value.getApi()
    calendarApi.removeAllEvents()
    events.forEach(event => {
      try {
        calendarApi.addEvent(event)
      } catch (error) {
        // Silently handle errors - event might be invalid
      }
    })
  }
}

// Legacy function for backwards compatibility
function updateCalendarEvents(tasks) {
  rawCalendarTasks.value = tasks
  updateCalendarDisplay()
}

// Helper function to build calendar URL with current state
function buildCalendarUrl() {
  let url = '/calendar'
  const params = []

  // Add view parameter
  if (currentView.value) {
    params.push(`view=${encodeURIComponent(currentView.value)}`)
  }

  // Add date parameter (YYYY-MM-DD format)
  if (currentDate.value) {
    let dateSource = currentDate.value
    if (currentView.value === 'month' && selectedDayDateKey.value) {
      const selectedDate = parseDateKeyToDate(selectedDayDateKey.value)
      if (selectedDate) {
        dateSource = selectedDate
      }
    }
    const dateStr = formatDateForCalendar(dateSource)
    params.push(`date=${encodeURIComponent(dateStr)}`)
  }
  if (selectedDayDateKey.value) {
    params.push(`selectedDay=${encodeURIComponent(selectedDayDateKey.value)}`)
  }

  // Add filter parameters
  if (selectedProjectIds.value.length > 0) {
    params.push(`projectIds=${encodeURIComponent(selectedProjectIds.value.join(','))}`)
  }
  params.push(`asAuthor=${filterAsAuthor.value}`)
  params.push(`asAssignee=${filterAsAssignee.value}`)
  params.push(`asSubscriber=${filterAsSubscriber.value}`)
  params.push(`showEvents=${showEvents.value}`)
  params.push(`showTaskDeadlines=${showTaskDeadlines.value}`)

  // Add chat_id and from_id
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)

  if (params.length > 0) url += '?' + params.join('&')
  return url
}

// Store calendar URL in sessionStorage
function storeCalendarUrl() {
  try {
    const calendarUrl = buildCalendarUrl()
    sessionStorage.setItem('calendarUrl', calendarUrl)
    // Clear conflicting URLs to prevent stale data
    sessionStorage.removeItem('dashboardUrl')
    // Note: We can't clear all project task URLs, but referrer check will handle priority
  } catch (e) {
    // Failed to store calendar URL - non-critical
  }
}

// Handle event click (navigate to task or show event details)
function handleEventClick(info) {
  const itemType = info.event.extendedProps.itemType

  if (itemType === 'task') {
    const taskIid = info.event.extendedProps.taskIid
    if (!taskIid) return

    const task = info.event.extendedProps.task
    const projectId = task?.project?.id
    if (!projectId) return

    // Build URL with query params using shared utility
    const url = buildTaskUrl(projectId, taskIid, {
      chatId: props.chatId,
      fromId: props.fromId,
    })

    // FullCalendar provides jsEvent which contains the original DOM event
    const mouseEvent = info.jsEvent
    if (mouseEvent) {
      // Use shared navigation utility with new tab support
      navigateToUrl(url, mouseEvent, {
        beforeNavigate: storeCalendarUrl,
      })
    } else {
      // Fallback: store URL and navigate in same window
      storeCalendarUrl()
      window.location.href = url
    }
  } else if (itemType === 'event') {
    // Open event modal for editing
    const event = info.event.extendedProps.event
    openEditEventModal(event)
  }
}

function formatTimeForDayDetails(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString(userLocale.value === 'ru' ? 'ru-RU' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function formatEventTimeRangeLabel(eventItem) {
  if (eventItem?.allDay) return s__('Calendar|All day')
  const startLabel = formatTimeForDayDetails(eventItem?.startAt)
  if (!eventItem?.endAt) return startLabel
  const endLabel = formatTimeForDayDetails(eventItem?.endAt)
  if (startLabel === '—' || endLabel === '—') return startLabel
  if (startLabel === endLabel) return startLabel
  return `${startLabel} - ${endLabel}`
}

function formatTaskTimeLabel(taskItem) {
  if (!hasTaskSpecificTime(taskItem)) return s__('Calendar|All day')
  return formatTimeForDayDetails(taskItem?.dueAt)
}

function truncateText(text, maxLength = 120) {
  if (!text) return '—'
  const normalized = String(text).replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 1)}…`
}

function formatPersonLabel(user) {
  if (!user) return '—'
  if (user.username) return `@${user.username}`
  if (user.name) return user.name
  return user.id ? `${s__('Calendar|User')} ${user.id}` : '—'
}

function formatEventParticipantsLabel(eventItem, maxVisible = 4) {
  const participants = (eventItem?.eventParticipants || [])
    .map(participant => formatPersonLabel(participant?.user))
    .filter(Boolean)
  if (participants.length === 0) return '—'
  const visible = participants.slice(0, maxVisible)
  const hiddenCount = participants.length - visible.length
  return hiddenCount > 0 ? `${visible.join(', ')} +${hiddenCount}` : visible.join(', ')
}

function buildProjectDashboardUrl(projectId) {
  if (!projectId) return '#'
  const query = new URLSearchParams()
  if (props.chatId) query.set('chat_id', props.chatId)
  if (props.fromId) query.set('from_id', props.fromId)
  const queryString = query.toString()
  return `/projects/${encodeURIComponent(projectId)}/dashboard${queryString ? `?${queryString}` : ''}`
}

function getEventRowAccentColor(eventItem) {
  if (!eventItem?.color) return EVENT_COLOR_MAP.blue
  return eventItem.color.startsWith('#')
    ? eventItem.color
    : EVENT_COLOR_MAP[eventItem.color] || EVENT_COLOR_MAP.blue
}

function eventIndicatorMeta(eventItem) {
  if (eventItem?.seriesId && eventItem?.detached) {
    return {
      icon: CalendarEventFillIcon,
      title: s__('CalendarEvent|Detached occurrence'),
      color: '#9ca3af',
    }
  }

  if (eventItem?.seriesId) {
    return {
      icon: CalendarWeekFillIcon,
      title: s__('CalendarEvent|Recurring series'),
      color: '#2aabee',
    }
  }

  return {
    icon: CalendarEventOutlineIcon,
    title: s__('CalendarEvent|Standalone event'),
    color: '#6b7280',
  }
}

function openTaskFromDayDetails(taskItem) {
  if (!taskItem?.iid || !taskItem?.project?.id) return
  const url = buildTaskUrl(taskItem.project.id, taskItem.iid, {
    chatId: props.chatId,
    fromId: props.fromId,
  })
  storeCalendarUrl()
  window.location.href = url
}

// Event modal handlers
function openCreateEventModal() {
  editingEvent.value = null
  // Set initial date to tomorrow, respecting user's timezone
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  eventInitialDate.value = tomorrow
  showEventModal.value = true
}

function openEditEventModal(event) {
  editingEvent.value = event
  eventInitialDate.value = null
  showEventModal.value = true
  // Note: readOnly prop is passed to modal based on event.canUpdate
}

function closeEventModal() {
  showEventModal.value = false
  editingEvent.value = null
  eventInitialDate.value = null
}

function handleEventSaved(savedEvent) {
  // Refetch calendar data to update the view
  if (refetch) {
    refetch()
  }
}

function handleEventDeleted(eventId) {
  // Refetch calendar data to update the view
  if (refetch) {
    refetch()
  }
}

function handleEventResponded(response) {
  if (response.success) {
    alertBanner.value = {
      title: s__('CalendarEvent|Response recorded'),
      message: response.message,
      type: response.type || 'success',
    }
    // Refetch calendar data to update the view
    if (refetch) {
      refetch()
    }
  }
}

// Handle calendar date navigation
let isUpdatingFromCalendar = false
let isInitialLoad = true
const dayNavigationLockDate = ref(null)

function extractDateFromCalendarView(viewType, startValue) {
  let newDate
  if (viewType === 'dayGridMonth') {
    const midRange = new Date(startValue)
    midRange.setDate(midRange.getDate() + 14)
    newDate = new Date(midRange.getFullYear(), midRange.getMonth(), 1)
  } else if (isWeekFullCalendarView(viewType)) {
    const weekStart = new Date(startValue)
    newDate = new Date(weekStart)
    newDate.setDate(newDate.getDate() + 3)
  } else {
    newDate = new Date(startValue)
  }
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

function applyCalendarApiState(calendarApi) {
  if (!calendarApi) return
  const viewType = calendarApi.view.type
  const newDate = extractDateFromCalendarView(viewType, calendarApi.view.currentStart)
  const newDateDay = new Date(newDate)
  newDateDay.setHours(0, 0, 0, 0)
  const currentDateDay = new Date(currentDate.value)
  currentDateDay.setHours(0, 0, 0, 0)

  if (currentDateDay.getTime() !== newDateDay.getTime()) {
    currentDate.value = newDate
  }

  if (currentView.value === 'month') {
    const selectedDate = parseDateKeyToDate(selectedDayDateKey.value)
    if (
      !selectedDate ||
      selectedDate.getFullYear() !== currentDate.value.getFullYear() ||
      selectedDate.getMonth() !== currentDate.value.getMonth()
    ) {
      syncSelectedDayToCurrentMonth(currentDate.value)
    }
  }

  const updatedViewKey = getCalendarViewKey(viewType)
  if (updatedViewKey && currentView.value !== updatedViewKey) {
    currentView.value = updatedViewKey
  }

  if (!isInitialLoad) {
    storeCalendarUrl()
  }
}

function isDatesSetMatchingNavigationTarget(arg) {
  if (
    !navigationState.isNavigating ||
    !navigationState.targetDate ||
    !arg?.view?.type ||
    !arg?.start
  ) {
    return true
  }

  const viewType = arg.view.type
  const targetDate = new Date(navigationState.targetDate)
  targetDate.setHours(0, 0, 0, 0)
  const callbackStart = new Date(arg.start)
  callbackStart.setHours(0, 0, 0, 0)

  if (viewType === 'timeGridDay') {
    return formatDateForCalendar(callbackStart) === formatDateForCalendar(targetDate)
  }

  if (isWeekFullCalendarView(viewType)) {
    const weekEnd = new Date(callbackStart)
    weekEnd.setDate(weekEnd.getDate() + 6)
    weekEnd.setHours(23, 59, 59, 999)
    return targetDate >= callbackStart && targetDate <= weekEnd
  }

  if (viewType === 'dayGridMonth') {
    const visibleMonthDate = extractDateFromCalendarView(viewType, callbackStart)
    return (
      visibleMonthDate.getFullYear() === targetDate.getFullYear() &&
      visibleMonthDate.getMonth() === targetDate.getMonth()
    )
  }

  return true
}

function handleDatesSet(arg) {
  const expectedFullCalendarView = getFullCalendarView(currentView.value)
  const liveCalendarApi = calendarRef.value?.getApi ? calendarRef.value.getApi() : null

  // Hard guard for month mode: if callback belongs to a different visible month than app state,
  // force the calendar back to the app month and ignore this callback.
  if (arg?.view?.type === 'dayGridMonth' && calendarRef.value?.getApi) {
    const callbackVisibleDate = extractDateFromCalendarView('dayGridMonth', new Date(arg.start))
    const expectedMonthDate = new Date(currentDate.value)
    expectedMonthDate.setDate(1)
    expectedMonthDate.setHours(0, 0, 0, 0)
    const isSameMonth =
      callbackVisibleDate.getFullYear() === expectedMonthDate.getFullYear() &&
      callbackVisibleDate.getMonth() === expectedMonthDate.getMonth()

    if (!isSameMonth) {
      const calendarApi = calendarRef.value.getApi()
      const expectedMonthString = formatDateForCalendar(expectedMonthDate)
      const liveVisibleDate = extractDateFromCalendarView(
        calendarApi.view.type,
        new Date(calendarApi.view.currentStart)
      )
      const liveSameMonth =
        liveVisibleDate.getFullYear() === expectedMonthDate.getFullYear() &&
        liveVisibleDate.getMonth() === expectedMonthDate.getMonth()

      if (!liveSameMonth) {
        calendarApi.gotoDate(expectedMonthString)
      }
      return
    }
  }

  // Ignore stale callbacks that do not represent the calendar's currently active range.
  // FullCalendar can emit delayed datesSet events for the previous range after gotoDate().
  if (
    liveCalendarApi?.view?.currentStart &&
    arg?.start &&
    arg?.view?.type === liveCalendarApi.view.type
  ) {
    const liveStart = formatDateForCalendar(new Date(liveCalendarApi.view.currentStart))
    const callbackStart = formatDateForCalendar(new Date(arg.start))
    if (liveStart !== callbackStart) {
      return
    }
  }

  // Ignore stale callbacks from a previously active view.
  // This happens when FullCalendar emits late datesSet for old view type.
  if (expectedFullCalendarView && arg?.view?.type !== expectedFullCalendarView) {
    if (calendarRef.value?.getApi) {
      const calendarApi = calendarRef.value.getApi()
      if (calendarApi.view.type !== expectedFullCalendarView) {
        calendarApi.changeView(expectedFullCalendarView, formatDateForCalendar(currentDate.value))
      }
    }
    return
  }

  // For explicit day-open action from week/month, ignore late day callbacks
  // that don't match the requested day.
  if (dayNavigationLockDate.value && arg?.view?.type === 'timeGridDay') {
    const callbackDate = formatDateForCalendar(new Date(arg.start))
    if (callbackDate !== dayNavigationLockDate.value) {
      if (calendarRef.value?.getApi) {
        const calendarApi = calendarRef.value.getApi()
        calendarApi.gotoDate(dayNavigationLockDate.value)
      }
      return
    }
  }

  // Ignore stale callbacks while explicit navigation is in progress.
  // This prevents a late callback from a previous period from overriding currentDate/query variables.
  if (!isDatesSetMatchingNavigationTarget(arg)) {
    if (navigationState.targetDate && calendarRef.value?.getApi) {
      const calendarApi = calendarRef.value.getApi()
      const targetDateString = formatDateForCalendar(new Date(navigationState.targetDate))
      const visibleDate = extractDateFromCalendarView(
        calendarApi.view.type,
        new Date(calendarApi.view.currentStart)
      )
      const targetDate = new Date(navigationState.targetDate)
      const isSameVisibleMonth =
        visibleDate.getFullYear() === targetDate.getFullYear() &&
        visibleDate.getMonth() === targetDate.getMonth()
      if (!isSameVisibleMonth) {
        calendarApi.gotoDate(targetDateString)
      }
    }
    return
  }

  if (isInitialLoad) {
    isInitialLoad = false
  }

  const viewType = arg.view.type
  applyCalendarApiState({
    view: {
      type: viewType,
      currentStart: arg.start,
    },
  })
  isUpdatingFromCalendar = false
}

// Helper function to format date as YYYY-MM-DD string (unambiguous for FullCalendar)
function formatDateForCalendar(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Navigation - use gotoDate() for reliable navigation
function prevPeriod() {
  if (!calendarRef.value?.getApi) return

  resetNavigationState()
  const calendarApi = calendarRef.value.getApi()
  navigationState.isNavigating = true
  dayNavigationLockDate.value = null
  if (currentView.value === 'day') {
    const targetDate = new Date(currentDate.value)
    targetDate.setDate(targetDate.getDate() - 1)
    targetDate.setHours(0, 0, 0, 0)
    const targetDateString = formatDateForCalendar(targetDate)
    dayNavigationLockDate.value = targetDateString
    navigationState.targetDate = new Date(targetDate)
    currentDate.value = targetDate
    selectedDayDateKey.value = targetDateString
    calendarApi.gotoDate(targetDateString)
    storeCalendarUrl()
    return
  }

  const targetDate = new Date(currentDate.value)
  if (currentView.value === 'week') {
    targetDate.setDate(targetDate.getDate() - 7)
  } else if (currentView.value === 'month') {
    const year = targetDate.getFullYear()
    const month = targetDate.getMonth()
    const monthTargetDate = new Date(year, month - 1, 1, 0, 0, 0, 0)
    navigationState.targetDate = new Date(monthTargetDate)
    currentDate.value = monthTargetDate
    syncSelectedDayToCurrentMonth(monthTargetDate)
    calendarApi.gotoDate(formatDateForCalendar(monthTargetDate))
    storeCalendarUrl()
    return
  } else {
    calendarApi.prev()
    applyCalendarApiState(calendarApi)
    return
  }

  targetDate.setHours(0, 0, 0, 0)
  navigationState.targetDate = new Date(targetDate)
  currentDate.value = targetDate
  const targetDateString = formatDateForCalendar(targetDate)
  selectedDayDateKey.value = targetDateString
  calendarApi.gotoDate(targetDateString)
  storeCalendarUrl()
}

function nextPeriod() {
  if (!calendarRef.value?.getApi) return

  resetNavigationState()
  const calendarApi = calendarRef.value.getApi()
  navigationState.isNavigating = true
  dayNavigationLockDate.value = null
  if (currentView.value === 'day') {
    const targetDate = new Date(currentDate.value)
    targetDate.setDate(targetDate.getDate() + 1)
    targetDate.setHours(0, 0, 0, 0)
    const targetDateString = formatDateForCalendar(targetDate)
    dayNavigationLockDate.value = targetDateString
    navigationState.targetDate = new Date(targetDate)
    currentDate.value = targetDate
    selectedDayDateKey.value = targetDateString
    calendarApi.gotoDate(targetDateString)
    storeCalendarUrl()
    return
  }

  const targetDate = new Date(currentDate.value)
  if (currentView.value === 'week') {
    targetDate.setDate(targetDate.getDate() + 7)
  } else if (currentView.value === 'month') {
    const year = targetDate.getFullYear()
    const month = targetDate.getMonth()
    const monthTargetDate = new Date(year, month + 1, 1, 0, 0, 0, 0)
    navigationState.targetDate = new Date(monthTargetDate)
    currentDate.value = monthTargetDate
    syncSelectedDayToCurrentMonth(monthTargetDate)
    calendarApi.gotoDate(formatDateForCalendar(monthTargetDate))
    storeCalendarUrl()
    return
  } else {
    calendarApi.next()
    applyCalendarApiState(calendarApi)
    return
  }

  targetDate.setHours(0, 0, 0, 0)
  navigationState.targetDate = new Date(targetDate)
  currentDate.value = targetDate
  const targetDateString = formatDateForCalendar(targetDate)
  selectedDayDateKey.value = targetDateString
  calendarApi.gotoDate(targetDateString)
  storeCalendarUrl()
}

function toggleMonthPicker() {
  if (!showMonthPicker.value) {
    monthPickerDate.value = new Date(currentDate.value)
  }

  showMonthPicker.value = !showMonthPicker.value
}

function handleMonthPickerSelect(value) {
  if (!value) return

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    goToMonth(value.getFullYear(), value.getMonth())
    showMonthPicker.value = false
    return
  }

  if (typeof value === 'object' && value !== null) {
    const year = Number(value.year)
    const monthIndex = Number(value.month)
    if (
      Number.isFinite(year) &&
      Number.isFinite(monthIndex) &&
      monthIndex >= 0 &&
      monthIndex <= 11
    ) {
      goToMonth(year, monthIndex)
      showMonthPicker.value = false
    }
  }
}

function goToMonth(year, monthIndex) {
  if (!calendarRef.value?.getApi) return

  const targetDate = new Date(year, monthIndex, 1, 0, 0, 0, 0)
  if (Number.isNaN(targetDate.getTime())) return

  resetNavigationState()
  const calendarApi = calendarRef.value.getApi()
  navigationState.isNavigating = true
  dayNavigationLockDate.value = null
  navigationState.targetDate = new Date(targetDate)
  currentDate.value = targetDate
  syncSelectedDayToCurrentMonth(targetDate)
  calendarApi.gotoDate(formatDateForCalendar(targetDate))
  storeCalendarUrl()
}

function handleDocumentPointerDown(event) {
  if (!showMonthPicker.value) return

  const header = periodHeaderRef.value
  if (!header) return

  if (!header.contains(event.target)) {
    showMonthPicker.value = false
  }
}

function goToToday() {
  if (!calendarRef.value?.getApi) return
  resetNavigationState()
  const calendarApi = calendarRef.value.getApi()
  dayNavigationLockDate.value = null
  const now = new Date()
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
  const todayDateString = formatDateForCalendar(todayDate)
  if (currentView.value === 'day') {
    dayNavigationLockDate.value = todayDateString
    currentDate.value = todayDate
    selectedDayDateKey.value = todayDateString
    calendarApi.gotoDate(todayDateString)
    storeCalendarUrl()
    return
  }
  navigationState.isNavigating = true
  navigationState.targetDate = new Date(todayDate)
  currentDate.value = new Date(todayDate)
  selectedDayDateKey.value = todayDateString
  calendarApi.gotoDate(todayDateString)
  storeCalendarUrl()
}

// Project filter
function handleProjectFilterChange(ids) {
  selectedProjectIds.value = ids
  // Mark as initialized so we don't auto-select all projects on refetch
  projectsInitialized.value = true
  // The watch on selectedProjectIds will handle the refetch with debouncing
  // No need to call refetch() directly here
}

// Dashboard URL with query params
const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

// Breadcrumbs
const breadcrumbsItems = computed(() => [
  { label: s__('Dashboard|Main'), url: dashboardUrl.value },
  { label: s__('Calendar|Calendar') },
])

// Update calendar when data changes (no need to watch separately, already handled in main watch)

// Full-width mode toggle
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)

function checkCanToggleFullWidth() {
  if (typeof window === 'undefined') return
  runAfterNextPaint(() => {
    canToggleFullWidth.value = window.innerWidth >= 1024
    isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
  })
}

function toggleFullWidth() {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
  refreshCalendarLayout()
}

function handleResize() {
  checkCanToggleFullWidth()
  if (!canToggleFullWidth.value && isFullWidth.value) {
    isFullWidth.value = false
    refreshCalendarLayout()
  }
}

// Watch for full-width mode changes and reinitialize calendar
watch(isFullWidth, () => {
  refreshCalendarLayout()
})

function initializeCalendarAfterMount() {
  if (!calendarRef.value?.getApi) return
  const calendarApi = calendarRef.value.getApi()
  const mountNow = new Date()
  const todayDate = new Date(
    mountNow.getFullYear(),
    mountNow.getMonth(),
    mountNow.getDate(),
    0,
    0,
    0,
    0
  )

  // Ensure currentView matches restoredState (in case it was changed)
  if (restoredState.view && restoredState.view !== currentView.value) {
    currentView.value = normalizeCalendarView(restoredState.view)
  }

  // Apply the view to FullCalendar
  const initialView = getFullCalendarView(currentView.value)
  if (initialView) {
    calendarApi.changeView(initialView)
  }

  // Check if we need to restore date (only if different from today)
  const defaultView = getDefaultView()
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
  const hasRestoredDate = restoredState.date.getTime() !== today.getTime()

  if (hasRestoredDate || restoredState.view !== defaultView) {
    isUpdatingFromCalendar = true
    navigationState.isNavigating = true
    navigationState.targetDate = currentDate.value

    const dateString = formatDateForCalendar(currentDate.value)
    calendarApi.gotoDate(dateString)
  } else {
    currentDate.value = todayDate
  }

  if (userTimezone.value) {
    calendarApi.setOption('timeZone', userTimezone.value)
  }

  if (userLocale.value) {
    calendarApi.setOption('locale', userLocale.value)
  }

  if (calendarEvents.value.length > 0) {
    if (isSummaryTilesMode.value) {
      renderCalendarEvents([])
      nextTick(() => {
        renderSummaryDayTiles()
      })
    } else {
      renderCalendarEvents(calendarEvents.value)
    }
  }

  runAfterTwoPaints(() => {
    resetNavigationState()
  })
}

onMounted(() => {
  isFullWidth.value = readFullWidthPreference()

  // Check mobile on mount and add resize listener
  checkMobile()
  checkXsScreen()
  // Initial button position will be set after DOM is ready (above)
  window.addEventListener('resize', checkMobile)
  window.addEventListener('resize', checkXsScreen)
  window.addEventListener('resize', handleResize)
  window.addEventListener('resize', updateButtonPosition)
  document.addEventListener('mousedown', handleDocumentPointerDown)
  document.addEventListener('touchstart', handleDocumentPointerDown, { passive: true })

  runAfterTwoPaints(() => {
    checkCanToggleFullWidth()
    if (!canToggleFullWidth.value && isFullWidth.value) {
      isFullWidth.value = false
    }
    refreshCalendarLayout()
    initializeCalendarAfterMount()
  })
})

watch([currentView, isMobile], ([view]) => {
  if (!calendarRef.value?.getApi) return

  const calendarApi = calendarRef.value.getApi()
  const expectedView = getFullCalendarView(view)
  if (expectedView && calendarApi.view.type !== expectedView) {
    calendarApi.changeView(expectedView, formatDateForCalendar(currentDate.value))
  }

  if (isSummaryTilesMode.value) {
    renderCalendarEvents([])
    nextTick(() => {
      renderSummaryDayTiles()
    })
  } else {
    renderCalendarEvents(calendarEvents.value)
  }
})

watch(selectedDayDateKey, () => {
  storeCalendarUrl()
  if (!isSummaryTilesMode.value) return
  nextTick(() => {
    renderSummaryDayTiles()
  })
})

onUnmounted(() => {
  // Remove resize listener
  window.removeEventListener('resize', checkMobile)
  window.removeEventListener('resize', checkXsScreen)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('resize', updateButtonPosition)
  document.removeEventListener('mousedown', handleDocumentPointerDown)
  document.removeEventListener('touchstart', handleDocumentPointerDown)
})
</script>

<style scoped>
/* Essential FullCalendar CSS */
:deep(.fc) {
  font-family:
    Arial,
    Helvetica Neue,
    Helvetica,
    sans-serif;
  font-size: 1em;
}

:deep(.fc-button) {
  background-color: #2c3e50;
  border-color: #2c3e50;
  color: #fff;
  padding: 0.4em 0.65em;
  border-radius: 0.25em;
  font-weight: 500;
  cursor: pointer;
}

:deep(.fc-button:hover) {
  background-color: #34495e;
  border-color: #34495e;
}

:deep(.fc-button-primary:not(:disabled):active),
:deep(.fc-button-primary:not(:disabled).fc-button-active) {
  background-color: #1a252f;
  border-color: #1a252f;
}

:deep(.fc-daygrid-day-frame) {
  min-height: 100px;
}

:deep(.fc-daygrid-day.fc-day-other .fc-daygrid-day-frame) {
  background: #f9fafb;
}

:deep(.fc-daygrid-day.fc-day-other .fc-daygrid-day-number) {
  color: #9ca3af;
}

:deep(.mobile-day-summary-tile) {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
  width: 100%;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}

:deep(.mobile-day-summary-tile:hover) {
  background: #f8fafc;
  border-color: #bfdbfe;
}

:deep(.mobile-day-summary-tile.is-selected) {
  background: #eff6ff;
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.25);
}

:deep(.mobile-day-summary-tile.is-outside-month) {
  cursor: not-allowed;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

:deep(.mobile-day-summary-tile.is-outside-month:hover) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

:deep(.mobile-day-summary-tile.is-outside-month .mobile-day-summary-date) {
  color: #9ca3af;
}

:deep(.mobile-day-summary-date) {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

:deep(.mobile-day-summary-counts) {
  width: 100%;
}

:deep(.mobile-day-summary-counts-single) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

:deep(.mobile-day-summary-counts-double) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  align-items: center;
  row-gap: 4px;
  column-gap: 6px;
}

:deep(.mobile-day-summary-double-date) {
  grid-column: 1;
  grid-row: 1;
  justify-self: center;
}

:deep(.mobile-day-summary-double-spacer) {
  grid-column: 2;
  grid-row: 1;
}

:deep(.mobile-day-summary-double-events) {
  grid-column: 1;
  grid-row: 2;
  justify-self: center;
}

:deep(.mobile-day-summary-double-deadlines) {
  grid-column: 2;
  grid-row: 2;
  justify-self: center;
}

:deep(.mobile-day-summary-chip) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  font-size: 11px;
  line-height: 1;
  padding: 0 6px;
  border-radius: 999px;
  font-weight: 600;
}

:deep(.mobile-day-summary-chip-events) {
  background: #dbeafe;
  color: #1e3a8a;
}

:deep(.mobile-day-summary-chip-deadlines) {
  background: #e0f2fe;
  color: #075985;
}

.mobile-summary-mode :deep(.fc-daygrid-day-top),
.mobile-summary-mode :deep(.fc-daygrid-day-events),
.mobile-summary-mode :deep(.fc-daygrid-day-bottom) {
  display: none !important;
}

.mobile-summary-mode :deep(.fc-daygrid-day-frame) {
  min-height: 62px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.day-details-panel {
  margin-top: 16px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  padding: 16px;
}

.day-details-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.day-details-title {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.day-details-date {
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
}

.day-details-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.day-details-section {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.day-details-section-title {
  margin: 0;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.day-details-empty {
  padding: 12px;
  color: #6b7280;
  font-size: 13px;
}

.day-details-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.day-details-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
}

.day-details-table th,
.day-details-table td {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;
  color: #111827;
}

.day-details-table th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6b7280;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
}

.day-details-row {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.day-details-row-event {
  border-left: 4px solid var(--day-details-row-accent, #4285f4);
}

.day-details-row:hover {
  background: #f8fafc;
}

.day-details-cell-ellipsis {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.day-details-project-link {
  color: #2aabee;
  text-decoration: none;
}

.day-details-project-link:hover {
  text-decoration: underline;
}

.day-details-event-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.day-details-event-indicator-icon {
  flex-shrink: 0;
  opacity: 0.9;
}

:deep(.fc-daygrid-event) {
  margin: 1px 2px 0;
  padding: 0 1px;
  border-radius: 3px;
  font-size: 0.85em;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}

:deep(.fc-daygrid-event .fc-event-main) {
  width: 100% !important;
  min-width: 0;
  overflow: hidden;
}

:deep(.fc-timegrid-event) {
  border-radius: 3px;
  font-size: 0.85em;
  min-width: 0;
}

:deep(.fc-timegrid-event .fc-event-main) {
  width: 100% !important;
  min-width: 0;
  overflow: hidden;
}

:deep(.fc-col-header-cell) {
  padding: 0.5em;
  background-color: #f8f9fa;
  font-weight: 600;
}

:deep(.fc-daygrid-day-number) {
  padding: 4px;
  color: #666;
}

:deep(.fc .fc-timegrid-col.fc-day-today) {
  background-color: #dbeafe;
}

:deep(.fc .fc-daygrid-day.fc-day-today) {
  background-color: #dbeafe !important;
}

:deep(.fc-daygrid-day-top) {
  flex-direction: row;
  justify-content: space-between;
}

/* Event participation status indicators */
:deep(.event-organizer) {
  border-left: 4px solid #f4b400;
  font-weight: 600;
}

:deep(.event-accepted) {
  border-left: 4px solid #0f9d58;
}

:deep(.event-pending) {
  border-left: 4px solid #f4b400;
  opacity: 0.85;
}

:deep(.event-declined) {
  border-left: 4px solid #db4437;
  opacity: 0.6;
  text-decoration: line-through;
}

/* Ensure border is visible in time grid view */
:deep(.fc-timegrid-event.event-organizer),
:deep(.fc-timegrid-event.event-accepted),
:deep(.fc-timegrid-event.event-pending),
:deep(.fc-timegrid-event.event-declined) {
  border-left-width: 4px;
  border-left-style: solid;
}

:deep(.fc-timegrid-event.event-organizer) {
  border-left-color: #f4b400;
}

:deep(.fc-timegrid-event.event-accepted) {
  border-left-color: #0f9d58;
}

:deep(.fc-timegrid-event.event-pending) {
  border-left-color: #f4b400;
  opacity: 0.85;
}

:deep(.fc-timegrid-event.event-declined) {
  border-left-color: #db4437;
  opacity: 0.6;
}

/* SVG icon styles for event participation status */
:deep(.event-icon) {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

/* Event content wrapper */
:deep(.event-content-wrapper) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 2px !important;
  width: 100% !important;
  min-width: 0 !important;
  overflow: hidden;
  box-sizing: border-box;
}

:deep(.event-content-wrapper span:first-child) {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;
}

/* Ensure event titles are visible */
:deep(.fc-event .event-content-wrapper span:not(:first-child)) {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  display: block !important;
  color: #ffffff !important;
  font-weight: 500 !important;
}

.calendar-container {
  padding: 24px;
  overflow: visible;
}

.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
}

.period-header {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
  padding: 8px 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.period-header-button {
  background: #f3f4f6;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 8px 16px;
  min-height: 40px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.period-header-button:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.period-header-button:focus-visible {
  outline: 2px solid #2aabee;
  outline-offset: 2px;
}

.period-nav-btn {
  min-width: 42px;
  min-height: 40px;
  padding: 0;
  font-size: 20px;
  line-height: 1;
}

.period-month-picker-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.12),
    0 8px 10px -6px rgba(0, 0, 0, 0.12);
  padding: 8px;
}

.period-month-picker-popover :deep(.dp__month_year_wrap),
.period-month-picker-popover :deep(.dp__selection_grid),
.period-month-picker-popover :deep(.dp__selection_grid_header) {
  margin-top: 0;
}

.period-month-picker-popover :deep(.dp__selection_grid_header button:hover),
.period-month-picker-popover :deep(.dp__selection_grid_header button:focus-visible),
.period-month-picker-popover :deep(.dp__selection_grid_header button:focus) {
  background: #e5e7eb !important;
  border-color: #9ca3af !important;
  color: #111827 !important;
  box-shadow: none !important;
}
.period-month-picker-popover :deep(.dp__selection_grid_header button:hover .dp__inner_nav),
.period-month-picker-popover :deep(.dp__selection_grid_header button:focus-visible .dp__inner_nav),
.period-month-picker-popover :deep(.dp__selection_grid_header button:focus .dp__inner_nav) {
  background: transparent !important;
}

.calendar-wrapper {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  font-size: 1.1rem;
  color: #666;
}

.calendar-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.calendar-header-title {
  margin: 0;
  text-align: center;
}

.calendar-full-width-toggle-btn {
  margin-left: auto;
}

.view-switcher {
  display: inline-flex;
  gap: 8px;
  padding: 4px;
  border-radius: 12px;
}

.view-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  transition: all 0.15s ease-in-out;
}

.view-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.view-btn.active {
  background: #2aabee;
  color: white;
  border-color: #2aabee;
  box-shadow:
    0 10px 15px -3px rgba(42, 171, 238, 0.2),
    0 4px 6px -2px rgba(42, 171, 238, 0.2);
}

.view-btn.active:hover {
  background: #229ed9;
  box-shadow:
    0 20px 25px -5px rgba(42, 171, 238, 0.3),
    0 10px 10px -5px rgba(42, 171, 238, 0.3);
}

.nav-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  transition: all 0.15s ease-in-out;
  min-width: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.filter-panel {
  margin-bottom: 16px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.expand-icon {
  font-size: 12px;
  color: #666;
}

.active-count {
  color: #1d6bf3;
  font-weight: 700;
}

.filter-content {
  padding: 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-x: hidden;
}

.filter-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.filter-label {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.info-icon-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: #666;
}

.info-icon-button:hover {
  background-color: #f0f0f0;
}

.info-icon-button:focus {
  outline: 2px solid #2aabee;
  outline-offset: 2px;
}

.filter-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.calendar-filter-checkboxes-row > .info-icon-button {
  min-width: 20px;
  width: 32px;
  height: 32px;
  min-height: 20px;
  padding: 0;
  margin-left: 12px;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.filter-checkbox:hover {
  background-color: #e0e0e0;
}

.events-checkbox {
  background: #dbeafe;
  color: #1e3a8a;
}

.events-checkbox:hover {
  background: #bfdbfe;
}

.deadlines-checkbox {
  background: #e0f2fe;
  color: #075985;
}

.deadlines-checkbox:hover {
  background: #bae6fd;
}

.filter-checkbox input[type='checkbox'] {
  margin: 0;
  cursor: pointer;
}

@media (max-width: 767px) {
  .calendar-filter-checkboxes-row {
    justify-content: space-between;
  }

  :deep(.multi-select .selected-chips) {
    min-width: 0;
    max-width: 100%;
    overflow-x: hidden;
  }

  :deep(.multi-select .selected-chip) {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.multi-select .option-item) {
    min-width: 0;
    overflow: hidden;
  }

  :deep(.multi-select .option-badge) {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-underlined-checklist .mobile-underlined-checklist-item.filter-checkbox {
    border-radius: 0;
    background: transparent;
    padding: 10px 12px;
    margin: 0;
    width: 100%;
  }
}

/* Keep old class for backwards compatibility */
.project-filter-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.project-filter-checkbox:hover {
  background-color: #e0e0e0;
}

.project-filter-checkbox input[type='checkbox'] {
  margin: 0;
  cursor: pointer;
}

.calendar-wrapper {
  margin-top: 16px;
}

/* FullCalendar custom styles for task deadlines (striped/hatched pattern) */
:deep(.task-deadline) {
  border-style: dashed !important;
  border-width: 2px !important;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 3px,
    rgba(255, 255, 255, 0.15) 3px,
    rgba(255, 255, 255, 0.15) 6px
  ) !important;
  cursor: pointer !important;
}

:deep(.task-deadline:hover) {
  opacity: 0.9;
}

:deep(.fc-event-task-deadline) {
  border-style: dashed !important;
}

/* Calendar events - solid background, rounded corners */
:deep(.calendar-event) {
  border-style: solid !important;
  border-width: 1px !important;
  border-radius: 4px !important;
  cursor: pointer !important;
}

:deep(.calendar-event:hover) {
  opacity: 0.9;
}

/* Different styling for events vs deadlines */
:deep(.fc-daygrid-event.task-deadline) {
  border-style: dashed !important;
  border-width: 2px !important;
}

:deep(.fc-daygrid-event.calendar-event) {
  border-style: solid !important;
  border-width: 1px !important;
}

/* Item type checkbox styling */
.item-type-checkbox {
  position: relative;
}

.checkbox-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 4px;
}

/* Floating add event button */
.add-event-btn {
  position: fixed;
  bottom: 20px;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(42, 171, 238, 0.3);
  transition: all 0.15s ease-in-out;
}

.add-event-btn:hover:not(:disabled) {
  background: #229ed9;
  box-shadow: 0 6px 16px rgba(42, 171, 238, 0.4);
}

.add-event-btn:active {
  transform: scale(0.98);
}

.add-event-text {
  white-space: nowrap;
}

/* On xs screens: show icon only, circular button */
@media (max-width: 640px) {
  .add-event-btn {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    padding: 0;
  }

  .add-event-text {
    display: none;
  }
}

/* On screens bigger than xs: show text, rounded button */
@media (min-width: 641px) {
  .add-event-btn {
    border-radius: 12px;
    padding: 12px 24px;
    min-height: auto;
  }
}

@media (max-width: 640px) {
  .calendar-header {
    gap: 8px;
  }

  .period-header {
    width: 100%;
    flex: 1 1 100%;
    display: grid;
    grid-template-columns: 20fr 60fr 20fr;
  }

  .view-switcher {
    flex-wrap: wrap;
  }

  .view-btn {
    font-size: 12px;
    padding: 4px 8px;
  }

  .day-details-panel {
    padding: 12px;
    border-radius: 10px;
  }

  .day-details-table {
    min-width: 760px;
  }
}

@media (max-width: 489px) {
  .calendar-container.mobile-summary-mode .calendar-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .calendar-container.mobile-summary-mode :deep(.fc),
  .calendar-container.mobile-summary-mode :deep(.fc .fc-scrollgrid) {
    min-width: 560px;
  }
}

@media (min-width: 768px) and (max-width: 1366px) {
  .mobile-summary-mode :deep(.fc-daygrid-day-frame) {
    min-height: 52px;
  }

  .mobile-summary-mode :deep(.mobile-day-summary-tile) {
    min-height: 0;
    padding: 3px 4px;
    gap: 3px;
    border-radius: 6px;
  }

  .mobile-summary-mode :deep(.mobile-day-summary-date) {
    font-size: 12px;
  }

  .mobile-summary-mode :deep(.mobile-day-summary-chip) {
    min-width: 18px;
    height: 16px;
    font-size: 10px;
    padding: 0 4px;
  }
}
</style>
